/**
 * BonusTracker.jsx — Admin-only Bonus Tracking System
 * Browning Electric / ElecBid
 *
 * Tracks monthly & quarterly performance bonuses for all active crew.
 * Auto-calculates metric deductions (late timesheets, missing notes,
 * incomplete visits) for auto-tracked employees from live Jobber data.
 * Custom items (one-offs, targets) supported for all employees.
 *
 * SQL to create the bonus_tracker table (run in Supabase SQL editor):
 *
 *   CREATE TABLE bonus_tracker (
 *     id BIGSERIAL PRIMARY KEY,
 *     employee_name TEXT NOT NULL,
 *     period_type TEXT NOT NULL CHECK (period_type IN ('monthly', 'quarterly')),
 *     period_start DATE NOT NULL,
 *     period_end DATE NOT NULL,
 *     base_bonus NUMERIC DEFAULT 500,
 *     auto_deductions JSONB DEFAULT '[]'::jsonb,
 *     custom_items JSONB DEFAULT '[]'::jsonb,
 *     final_bonus NUMERIC,
 *     admin_notes TEXT,
 *     quarterly_review TEXT,
 *     created_at TIMESTAMPTZ DEFAULT NOW(),
 *     updated_at TIMESTAMPTZ DEFAULT NOW(),
 *     created_by TEXT,
 *     UNIQUE(employee_name, period_type, period_start)
 *   );
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../lib/supabase";
import { computeBonusRows, buildJobTechHoursMap, buildRoleByFirst, isBonusExcluded, buildPaperworkHoursIndex, owesPaperwork } from "../lib/profitBonus";
// Referral payouts come from the SAME engine as the Referral Leaderboard —
// referral money must never be routed through computeBonusRows (Taylor is in
// BONUS_EXCLUDED_FIRST there and would silently vanish).
import { buildReferralRows, monthKeyOf as referralMonthKey, parseJobDate as referralParseDate } from "../lib/referralPayout";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import ReferralBonuses from "./ReferralBonuses";


// ── Active employees ──────────────────────────────────────────────────────────
// Hardcoded *fallback* — preserves the historical roster order (owner →
// ops lead → journeymen → apprentices → admin). Used only when jobber_users
// hasn't loaded yet OR a tech doesn't appear in Jobber. The "live" list is
// `activeEmployees` (a useMemo inside the component) which auto-discovers
// any new active jobber_user not already in this fallback and appends them
// at the end of the order. So when a new apprentice gets added in Jobber,
// they show up on the Bonus Tracker on the next refresh without a deploy.
const ACTIVE_EMPLOYEES_FALLBACK = [
  "Dylan", "Colton", "Kyle", "Landon", "Morgan",
  "Jon", "Justin", "Taylor", "Tommy", "Ayden", "Cindy", "Pippin",
];

// ── Owner/family carve-outs: never earn review bonuses, even manually ────────
// (The engine's isBonusExcluded also contains "taylor", but Taylor P DOES get
// paid on a deliberate manual review credit — so the hard floor is this list.)
const OWNER_EXCLUDED_FIRST = ["dylan", "colton", "cindy"];

// ── Non-crew staff: NEVER auto-credited for reviews ──────────────────────────
// Taylor Priegnitz is admin/dispatch and "Taylor & Mallory" (nabocreative) is
// the social-media agency's shared Jobber login. Review comments thanking
// "Taylor" are about scheduling/CSR work, not a tech on the job — so the
// review-bonus math skips these names for auto attribution AND the mention
// re-scan. A manual assigned_techs override still pays out (that's a human
// decision). Mirrors api/_knownTechs.js NON_CREW_FIRST + CrewPerformance.jsx.
const NON_CREW_FIRST = ["taylor", "mallory"];

// ── Employees with auto-tracked Jobber metrics ────────────────────────────────
// Deductions (missing notes / late timesheets / visit completion) ONLY apply to
// the $500-performance-bonus crew — Kyle and Tommy. Regular hourly-payroll techs
// (Morgan, Jon, Justin, Taylor, Ayden, apprentices) are NOT docked: their note
// "misses" were mostly their lead writing the job note or next-day entries, and
// you can't claw back regular payroll. They still earn profit/crew bonuses; they
// just aren't on the deduction tracker. (Per Dylan, 2026-06-08.)
const AUTO_TRACKED = ["Kyle", "Tommy"];

// ── Employees who receive a $500 base bonus ───────────────────────────────────
const BASE_500_EMPLOYEES = new Set(["Kyle", "Tommy"]);

// ── Deduction caps per metric ─────────────────────────────────────────────────
const METRIC_CONFIG = {
  timesheet_rate:    { label: "Same-Day Timesheet",  per_miss: 10, cap: 200 },
  note_rate:         { label: "Notes Per Visit",      per_miss: 10, cap: 150 },
  visit_completion:  { label: "Visit Completion",     per_miss: 10, cap: 150 },
};

// ── Profit-bonus fallback settings (used only when app_settings is unset) ────
// The real values come from app_settings (same keys the CrewPerformance
// leaderboard reads); these mirror the leaderboard defaults so both surfaces
// agree out of the box. All qualifying / excess / lead+crew math now lives in
// the shared engine (src/lib/profitBonus.js).
const PROFIT_BONUS_REV_MIN = 5500;
const PROFIT_BONUS_PCT_MIN = 50;
const PROFIT_BONUS_DEFAULT_PCT = 10;
const PROFIT_BONUS_CREW_PCT = 5;

// ── Review-bonus defaults (overridable via app_settings) ─────────────────────
// Mirrors CrewPerformance leaderboard's "Review Bonus" tile so both surfaces
// agree out of the box. Keys: review_bonus_base / review_bonus_mention /
// review_bonus_monthly_cap.
const REVIEW_BONUS_BASE_DEFAULT        = 25;
const REVIEW_BONUS_MENTION_DEFAULT     = 25;
const REVIEW_BONUS_MONTHLY_CAP_DEFAULT = 300;

// ── Brand colours (mirrored from CrewPerformance) ─────────────────────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.10)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.10)",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.10)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.10)",
  purple: "#a78bfa",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function normName(name) {
  if (!name) return "";
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}
function firstName(name) {
  if (!name) return "";
  return name.trim().split(/\s+/)[0].toLowerCase();
}
function parseDate(str) {
  if (!str || str === "-" || str === "") return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}
function sameCalendarDay(d1, d2) {
  if (!d1 || !d2) return false;
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

// ── Period utilities ──────────────────────────────────────────────────────────
function monthStart(year, month) {
  return new Date(year, month, 1);
}
function monthEnd(year, month) {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}
function quarterMonths(year, quarter) {
  // quarter 0-indexed: 0=Q1(Jan-Mar), 1=Q2(Apr-Jun), 2=Q3(Jul-Sep), 3=Q4(Oct-Dec)
  const startMonth = quarter * 3;
  return [startMonth, startMonth + 1, startMonth + 2];
}
function quarterStart(year, quarter) {
  return monthStart(year, quarter * 3);
}
function quarterEnd(year, quarter) {
  return monthEnd(year, quarter * 3 + 2);
}
function toISODate(d) {
  return d.toISOString().slice(0, 10);
}
function currentYear() { return new Date().getFullYear(); }
function currentMonth() { return new Date().getMonth(); }
function currentQuarter() { return Math.floor(new Date().getMonth() / 3); }

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── Auto-deduction calculator ─────────────────────────────────────────────────
// Local calendar day string — matches CrewPerformance's metric_overrides keys.
function localDayISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Computes auto-deductions for a given employee within a date range.
 * Returns an array matching the auto_deductions JSONB format.
 * metricOverrides (metric_overrides rows) let an admin EXCUSE a flagged miss
 * (dropped from the deduction) or ADD one the automation missed — mirrors the
 * CrewPerformance boards so the two surfaces always agree on the same misses.
 */
function calcAutoDeductions(techFirstName, timesheets, visits, notesLookup, fromDate, toDate, metricOverrides = [], hoursIndex = null) {
  const tf = techFirstName.toLowerCase();

  // metric → excuse keys (`${target||""}|${day}`) and adds within the period.
  const excused = new Set();
  const adds = { timesheet: [], note: [], visit_completion: [] };
  for (const o of metricOverrides) {
    if (o.tech_first !== tf) continue;
    if (!(o.metric in adds)) continue; // request/general boards don't feed deductions
    if (o.action === "excuse") {
      excused.add(`${o.metric}|${o.target_key || ""}|${o.day}`);
    } else if (o.action === "add_miss") {
      const d = new Date(`${o.day}T12:00:00`);
      if (!isNaN(d.getTime()) && d >= fromDate && d <= toDate) adds[o.metric].push(o);
    }
  }
  const seenTsDedupKey = new Set();
  const periodTs = timesheets.filter(ts => {
    if (!ts.user_name) return false;
    if (firstName(ts.user_name) !== tf) return false;
    if (!ts.job_number) return false; // exclude general clock-ins
    const titleLower = (ts.job_title || "").toLowerCase();
    if (titleLower.includes("travel") || titleLower.includes("drive") || titleLower.includes("driving")) return false;
    const d = parseDate(ts.start_at);
    if (!d) return false;
    if (!(d >= fromDate && d <= toDate)) return false;
    // dedup: 1 entry per (job, calendar day)
    const dedupKey = `${String(ts.job_number)}|${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (seenTsDedupKey.has(dedupKey)) return false;
    seenTsDedupKey.add(dedupKey);
    return true;
  });

  const periodVisits = visits.filter(v => {
    if (!v.is_complete) return false;
    const assigned = (v.assigned_users || []).some(u => firstName(u.name) === tf);
    if (!assigned) return false;
    const d = parseDate(v.completed_at || v.start_at);
    if (!d) return false;
    return d >= fromDate && d <= toDate;
  });

  // Metric 1: Late timesheets
  let tsMisses = 0;
  const tsMissedItems = [];
  periodTs.forEach(ts => {
    const startDate = parseDate(ts.start_at);
    const createdDate = parseDate(ts.created_at);
    if (startDate && createdDate && !sameCalendarDay(startDate, createdDate)) {
      if (excused.has(`timesheet|${String(ts.job_number)}|${localDayISO(startDate)}`)) return;
      tsMisses++;
      tsMissedItems.push({
        date: ts.start_at?.slice(0, 10) ?? "—",
        submitted: ts.created_at?.slice(0, 10) ?? "—",
      });
    }
  });
  adds.timesheet.forEach(o => {
    tsMisses++;
    tsMissedItems.push({ date: o.day, submitted: o.note ? `manual: ${o.note}` : "manual (admin-added)" });
  });
  const tsDeduction = Math.min(tsMisses * METRIC_CONFIG.timesheet_rate.per_miss, METRIC_CONFIG.timesheet_rate.cap);

  // Metric 2: Notes per visit misses — same logic as CrewPerformance visitNoteMetrics
  // Requires the tech to have written a note on the same calendar day as the visit.
  // Deduplicates: one expected note per (job, tech, calendar date).
  let noteMisses = 0;
  const noteMissedItems = [];
  const seenNoteKey = new Set();
  periodVisits.forEach(v => {
    const jobNum = String(v.job_number || "");
    const visitStartDate = parseDate(v.start_at);
    const dateStr = visitStartDate
      ? `${visitStartDate.getFullYear()}-${visitStartDate.getMonth()}-${visitStartDate.getDate()}`
      : "unknown";
    const dedupKey = `${jobNum}|${tf}|${dateStr}`;
    if (seenNoteKey.has(dedupKey)) return;
    seenNoteKey.add(dedupKey);

    // Only charge people who actually worked the job that day — see
    // owesPaperwork in lib/profitBonus.js. Grandfathered before 2026-08-01 so
    // already-sent months keep the numbers the crew was given.
    if (!owesPaperwork({ jobNumber: jobNum, techFirst: tf, date: visitStartDate, hoursIndex })) return;

    const jobNotes = notesLookup.get(jobNum);
    let hasNote = false;
    if (jobNotes) {
      const match = jobNotes.find(n => {
        if (n.techFirst !== tf) return false;
        if (!visitStartDate || !n.createdAt) return true;
        return sameCalendarDay(n.createdAt, visitStartDate);
      });
      if (match) hasNote = true;
    }
    if (!hasNote) {
      if (visitStartDate && excused.has(`note|${jobNum}|${localDayISO(visitStartDate)}`)) return;
      noteMisses++;
      noteMissedItems.push({
        jobNumber: v.job_number ?? "—",
        date: (v.completed_at ?? v.start_at)?.slice(0, 10) ?? "—",
      });
    }
  });
  adds.note.forEach(o => {
    noteMisses++;
    noteMissedItems.push({ jobNumber: o.target_key ?? "—", date: o.note ? `${o.day} (manual: ${o.note})` : `${o.day} (manual)` });
  });
  const noteDeduction = Math.min(noteMisses * METRIC_CONFIG.note_rate.per_miss, METRIC_CONFIG.note_rate.cap);

  // Metric 3: Visit completion misses (completed_at not same day as start_at)
  let vcMisses = 0;
  const vcMissedItems = [];
  periodVisits.forEach(v => {
    const startDate = parseDate(v.start_at);
    const completedDate = parseDate(v.completed_at);
    // Same gate as the note metric: assigned-but-didn't-work doesn't own the close.
    if (!owesPaperwork({ jobNumber: String(v.job_number || ""), techFirst: tf, date: startDate, hoursIndex })) return;
    const vcExcused = startDate && excused.has(`visit_completion|${String(v.job_number)}|${localDayISO(startDate)}`);
    if (!startDate || !completedDate) {
      if (vcExcused) return;
      vcMisses++;
      vcMissedItems.push({
        jobNumber: v.job_number ?? "—",
        scheduled: v.start_at?.slice(0, 10) ?? "—",
        completed: v.completed_at?.slice(0, 10) ?? "—",
      });
      return;
    }
    if (!sameCalendarDay(startDate, completedDate)) {
      if (vcExcused) return;
      vcMisses++;
      vcMissedItems.push({
        jobNumber: v.job_number ?? "—",
        scheduled: v.start_at?.slice(0, 10) ?? "—",
        completed: v.completed_at?.slice(0, 10) ?? "—",
      });
    }
  });
  adds.visit_completion.forEach(o => {
    vcMisses++;
    vcMissedItems.push({ jobNumber: o.target_key ?? "—", scheduled: o.day, completed: o.note ? `manual: ${o.note}` : "manual (admin-added)" });
  });
  const vcDeduction = Math.min(vcMisses * METRIC_CONFIG.visit_completion.per_miss, METRIC_CONFIG.visit_completion.cap);

  return [
    {
      metric: "timesheet_rate",
      label: METRIC_CONFIG.timesheet_rate.label,
      misses: tsMisses,
      per_miss: METRIC_CONFIG.timesheet_rate.per_miss,
      cap: METRIC_CONFIG.timesheet_rate.cap,
      total: tsDeduction,
      total_entries: periodTs.length,
      items: tsMissedItems,
    },
    {
      metric: "note_rate",
      label: METRIC_CONFIG.note_rate.label,
      misses: noteMisses,
      per_miss: METRIC_CONFIG.note_rate.per_miss,
      cap: METRIC_CONFIG.note_rate.cap,
      total: noteDeduction,
      total_entries: periodVisits.length,
      items: noteMissedItems,
    },
    {
      metric: "visit_completion",
      label: METRIC_CONFIG.visit_completion.label,
      misses: vcMisses,
      per_miss: METRIC_CONFIG.visit_completion.per_miss,
      cap: METRIC_CONFIG.visit_completion.cap,
      total: vcDeduction,
      total_entries: periodVisits.length,
      items: vcMissedItems,
    },
  ];
}

/** Build notes lookup: Map<job_number, Array<{techNorm, techFirst, createdAt}>> */
function buildNotesLookup(notes) {
  const map = new Map();
  notes.forEach(n => {
    // Count a note if it has text OR ≥1 photo/file attachment (photo-only notes
    // are still documentation). Mirrors CrewPerformance notesLookup.
    const hasContent = n.message?.trim() || Number(n.attachment_count) > 0;
    if (!n.job_number || !n.created_by_name || !hasContent) return;
    const key = String(n.job_number);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({
      techNorm: normName(n.created_by_name),
      techFirst: firstName(n.created_by_name),
      createdAt: parseDate(n.created_at),
    });
  });
  return map;
}

/** A profit-bonus row contributes to the working total only when:
 *   - clientPaid: the client has paid us for the underlying job
 *     (auto-derived from jb_jobs.derived_status; defaults to true for
 *     pre-clientPaid records so old data isn't silently dropped)
 *     — OR payAnyway: an admin explicitly released this bonus before the
 *     client paid (profit_bonus_overrides.pay_anyway, for slow-paying
 *     commercial clients)
 *   - !paid: the bonus hasn't been pushed to QB / paid out yet
 *  This is the "ready to push to QB" set. */
function isProfitBonusPayable(b) {
  if (!b || b.paid) return false;
  if (Math.abs(Number(b.bonus) || 0) <= 0.005) return false;
  if (b.manual) {
    // A custom $ replacing a QUALIFYING job's computed bonus changes the
    // AMOUNT, not the release rule — it still waits for the client to pay
    // (or pay-anyway). Pure adjustments on non-qualifying jobs stay payable
    // immediately: those are deliberate one-off decisions.
    if (!b.qualifies) return true;
    const cp = b.clientPaid === undefined ? true : !!b.clientPaid;
    return cp || !!b.payAnyway;
  }
  // Otherwise only qualifying jobs with an actual amount, once the client paid
  // (or an admin overrode the client-paid gate).
  if (!b.qualifies) return false;
  const clientPaid = b.clientPaid === undefined ? true : !!b.clientPaid;
  return clientPaid || !!b.payAnyway;
}

/** Calculate final bonus from a record object. */
function calcFinalBonus(record, profitBonuses = [], reviewBonus = null, referralBonus = null) {
  const base = Number(record.base_bonus) || 0;
  const autoDeduct = (record.auto_deductions || []).reduce((s, d) => s + (Number(d.total) || 0), 0);
  const customAdds = (record.custom_items || []).filter(i => i.type === "bonus").reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const customDeducts = (record.custom_items || []).filter(i => i.type === "deduction").reduce((s, i) => s + Math.abs(Number(i.amount) || 0), 0);
  // Payable rows PLUS rows already paid: placement pins a paid row to the
  // month whose batch paid it, so a paid row shown in this view belongs in
  // this month's total. Without this, sending a batch (which stamps rows paid)
  // made the live card DROP those rows — Kyle's June card read $265 while the
  // sent record was $369.99 because the $105 SNDL row went paid.
  const profit = profitBonuses.filter(b => isProfitBonusPayable(b) || b.paid).reduce((s, b) => s + (Number(b.bonus) || 0), 0);
  const review = Number(reviewBonus?.total) || 0;
  // Referral payouts (leaderboard engine, bill-month bucketing) — same
  // payable+paid inclusion rule as profit rows.
  const referral = Number(referralBonus?.total) || 0;
  return base + customAdds + profit + review + referral - autoDeduct - customDeducts;
}

// ── Shared UI atoms ───────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
      {children}
    </div>
  );
}

function Pill({ children, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: `${color}22`, color,
    }}>
      {children}
    </span>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "7px 10px", borderRadius: 7,
  border: `1px solid ${C.border}`, background: C.card,
  color: C.text, fontSize: 13,
};

const btnStyle = (variant = "primary") => ({
  padding: "7px 14px", borderRadius: 7, border: "none",
  cursor: "pointer", fontSize: 12, fontWeight: 600,
  background: variant === "primary" ? C.accent : variant === "danger" ? C.redDim : C.surface,
  color: variant === "primary" ? "#0d0f12" : variant === "danger" ? C.red : C.muted,
  transition: "opacity 0.15s",
});

// ── Profit-bonus row (one job: computed lead/crew bonus OR a manual adj) ─────
function ProfitBonusRow({ bonus, readOnly, onUpdate, onExcludeAll, onExcludeEmployee }) {
  const isManual = !!bonus.manual;
  // Crew payouts are driven by the shared crew_pct + per-member weight, not a
  // per-member %, so the % field is only meaningful (and editable) for leads.
  const isLead = bonus.role !== "crew";
  const [pctInput, setPctInput] = useState(String(bonus.bonusPct ?? ""));
  const [amtInput, setAmtInput] = useState(bonus.manualAmount != null ? String(bonus.manualAmount) : "");
  // Custom-$ entry on a COMPUTED row: converts it to a manual override that
  // replaces the formula amount (the engine + server audit both honor
  // profit_bonus_overrides.manual_amount on lead AND crew rows).
  const [customAmtInput, setCustomAmtInput] = useState("");
  const [noteInput, setNoteInput] = useState(bonus.notes ?? "");
  const [paidState, setPaidState] = useState(!!bonus.paid);
  const [payAnywayState, setPayAnywayState] = useState(!!bonus.payAnyway);
  const [savingState, setSavingState] = useState(false);
  const [showExclude, setShowExclude] = useState(false);

  // Resync if upstream row changes (period switch / refresh)
  useEffect(() => {
    setPctInput(String(bonus.bonusPct ?? ""));
    setAmtInput(bonus.manualAmount != null ? String(bonus.manualAmount) : "");
    setCustomAmtInput("");
    setNoteInput(bonus.notes ?? "");
    setPaidState(!!bonus.paid);
    setPayAnywayState(!!bonus.payAnyway);
  }, [bonus.jobNum, bonus.role, bonus.bonusPct, bonus.manualAmount, bonus.notes, bonus.paid, bonus.payAnyway]);

  const dirty = isManual
    ? (Number(amtInput) !== Number(bonus.manualAmount) || (noteInput || "") !== (bonus.notes || "") || paidState !== !!bonus.paid || payAnywayState !== !!bonus.payAnyway)
    : ((isLead && Number(pctInput) !== Number(bonus.bonusPct)) || customAmtInput.trim() !== "" || paidState !== !!bonus.paid || payAnywayState !== !!bonus.payAnyway);

  async function handleSave() {
    setSavingState(true);
    try {
      if (isManual) {
        const n = Number(amtInput);
        if (isNaN(n)) return;
        // pay_anyway must persist here too — a custom $ on a QUALIFYING job is
        // gated on client-paid, and this checkbox is its only release valve.
        await onUpdate(bonus.jobNum, bonus.techFirst, bonus.role, { manual_amount: n, notes: noteInput || null, paid: paidState, pay_anyway: payAnywayState });
      } else {
        const patch = { paid: paidState, pay_anyway: payAnywayState };
        if (isLead) {
          const n = Number(pctInput);
          if (isNaN(n) || n < 0 || n > 100) return;
          patch.bonus_pct = n;
        }
        const custom = customAmtInput.trim();
        if (custom !== "") {
          const n = Number(custom);
          if (isNaN(n)) return;
          patch.manual_amount = n;
          // Keep the computed figure in the note so the history stays legible
          // after the row re-renders as a manual override.
          patch.notes = `custom amount — computed was $${Math.abs(Number(bonus.bonus) || 0).toFixed(0)}`;
        }
        await onUpdate(bonus.jobNum, bonus.techFirst, bonus.role, patch);
      }
    } finally {
      setSavingState(false);
    }
  }

  async function handleRemoveManual() {
    setSavingState(true);
    try {
      // Clear the manual amount + note → row reverts to its computed value
      // (typically $0 / non-qualifying).
      await onUpdate(bonus.jobNum, bonus.techFirst, bonus.role, { manual_amount: null, notes: null });
    } finally {
      setSavingState(false);
    }
  }

  const dateStr = bonus.closedDate
    ? new Date(bonus.closedDate).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
    : "--";

  const amt = Number(bonus.bonus) || 0;
  const isDeduction = isManual && amt < 0;
  // Crew (read-only) see a corrected amount on a QUALIFYING job exactly like a
  // computed Lead/Crew row: the correction is internal (a cost that missed the
  // job, a missing labour rate). Only admins see the "Manual" marking and its
  // note. Deductions and one-off adjustments on non-qualifying jobs stay
  // labelled so the tech knows why.
  const showManual = isManual && !(readOnly && bonus.qualifies && amt > 0);
  // Lead line reads "$excess × pct%". For a corrected row shown as computed, use
  // the excess the corrected amount implies so the line adds up.
  const leadPct = Number(pctInput || 0);
  const shownExcess = (isManual && !showManual && leadPct > 0) ? amt / (leadPct / 100) : (Number(bonus.excess) || 0);
  // Auto-derived from Jobber invoice rollup; not editable on this row.
  const clientPaid = bonus.clientPaid === undefined ? true : !!bonus.clientPaid;
  // Pay-anyway override makes an unpaid-client row behave as payable.
  const effectiveClientPaid = clientPaid || (!showManual && payAnywayState);

  // Row colours: manual adjustments use blue (bonus) / red (deduction); computed
  // bonuses use green (payable) / yellow (awaiting client) / gray (paid out).
  const rowBg = paidState ? `${C.border}25`
    : isDeduction ? C.redDim
    : showManual ? C.blueDim
    : !effectiveClientPaid ? `${C.yellow}15`
    : C.greenDim;
  const rowBorder = paidState ? C.border
    : isDeduction ? `${C.red}40`
    : showManual ? `${C.blue}40`
    : !effectiveClientPaid ? `${C.yellow}40`
    : `${C.green}30`;

  const tag = showManual ? (isDeduction ? "Deduction" : "Manual") : (isLead ? "Lead" : "Crew");
  const tagColor = showManual ? (isDeduction ? C.red : C.purple) : (isLead ? C.accent : C.blue);
  const amtColor = paidState ? C.muted
    : isDeduction ? C.red
    : (!showManual && !effectiveClientPaid) ? C.muted
    : C.green;

  return (
    <div style={{
      background: rowBg,
      border: `1px solid ${rowBorder}`,
      borderRadius: 8, padding: "8px 12px",
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              flexShrink: 0, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
              padding: "1px 6px", borderRadius: 4,
              background: `${tagColor}22`, color: tagColor,
            }}>
              {tag}
            </span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              #{bonus.jobNum} · {bonus.client || "—"}
            </span>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>{dateStr} · ${Math.round(bonus.revenue).toLocaleString("en-CA", { maximumFractionDigits: 0 })} rev · {bonus.profitPct.toFixed(1)}% margin</span>
            {bonus.carried && (
              <span style={{ color: C.purple, fontWeight: 700 }} title="This bonus wasn't payable when its month was sent to payroll — it follows each month until it's paid out.">
                · Carried from {bonus.carriedFrom}
              </span>
            )}
            {paidState ? (
              <span style={{ color: C.muted, fontWeight: 700 }}>· Paid out</span>
            ) : showManual ? (
              <span style={{ color: isDeduction ? C.red : C.purple, fontWeight: 700 }}>· Manual adjustment</span>
            ) : clientPaid ? (
              <span style={{ color: C.green, fontWeight: 700 }}>· ✓ Client paid</span>
            ) : payAnywayState ? (
              <span style={{ color: C.green, fontWeight: 700 }}>
                · Pay anyway — client hasn't paid{bonus.amountOutstanding > 0 ? ` ($${Math.round(bonus.amountOutstanding).toLocaleString("en-CA", { maximumFractionDigits: 0 })} out)` : ""}
              </span>
            ) : (
              <span style={{ color: C.yellow, fontWeight: 700 }}>
                · Awaiting client{bonus.amountOutstanding > 0 ? ` ($${Math.round(bonus.amountOutstanding).toLocaleString("en-CA", { maximumFractionDigits: 0 })} out)` : ""}
              </span>
            )}
          </div>
          {showManual && bonus.notes && (
            <div style={{ fontSize: 11, color: C.text, marginTop: 3, fontStyle: "italic", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              “{bonus.notes}”
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: amtColor }}>
            {paidState ? "" : (isDeduction ? "−" : "+")}${Math.abs(amt).toFixed(0)}
          </div>
          <div style={{ fontSize: 10, color: C.muted }}>
            {showManual ? (
              <>manual {isDeduction ? "deduction" : "bonus"}</>
            ) : isLead ? (
              <>${Math.round(shownExcess).toLocaleString("en-CA", { maximumFractionDigits: 0 })} × {leadPct}%{bonus.overridden ? "*" : ""}</>
            ) : (
              <>crew pool {Number(bonus.bonusPct || 0)}% · {Math.round((Number(bonus.weight) || 0) * 100)}% share</>
            )}
          </div>
        </div>
      </div>
      {!readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {isManual ? (
            <>
              <span style={{ fontSize: 11, color: C.muted }}>$:</span>
              <input
                type="number" step="1"
                value={amtInput}
                onChange={e => setAmtInput(e.target.value)}
                title="Dollar amount. Negative = a deduction."
                style={{ ...inputStyle, width: 80, padding: "4px 8px", fontSize: 12 }}
              />
              <input
                placeholder="reason / note"
                value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: 120, padding: "4px 8px", fontSize: 12 }}
              />
            </>
          ) : (
            <>
              {isLead && (
                <>
                  <span style={{ fontSize: 11, color: C.muted }}>%:</span>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={pctInput}
                    onChange={e => setPctInput(e.target.value)}
                    style={{ ...inputStyle, width: 70, padding: "4px 8px", fontSize: 12 }}
                  />
                </>
              )}
              <span style={{ fontSize: 11, color: C.muted }}>$:</span>
              <input
                type="number" step="1"
                placeholder="custom"
                value={customAmtInput}
                onChange={e => setCustomAmtInput(e.target.value)}
                title="Replace this computed bonus with a custom dollar amount. Save converts the row to a manual override; Remove on that row restores the formula."
                style={{ ...inputStyle, width: 80, padding: "4px 8px", fontSize: 12 }}
              />
            </>
          )}
          <label
            style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted, cursor: "pointer" }}
            title="Check after this bonus has been pushed to QB so it stops counting toward the next payout."
          >
            <input
              type="checkbox" checked={paidState}
              onChange={e => setPaidState(e.target.checked)}
            /> paid out
          </label>
          {(!isManual || bonus.qualifies) && !clientPaid && (
            <label
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: payAnywayState ? C.green : C.muted, cursor: "pointer" }}
              title="Release this bonus for payout even though the client hasn't paid yet — for commercial clients on long payment terms."
            >
              <input
                type="checkbox" checked={payAnywayState}
                onChange={e => setPayAnywayState(e.target.checked)}
              /> pay anyway
            </label>
          )}
          <button
            onClick={handleSave}
            disabled={!dirty || savingState}
            style={{
              ...btnStyle("secondary"),
              padding: "3px 10px", fontSize: 11,
              opacity: (!dirty || savingState) ? 0.5 : 1,
              cursor: (!dirty || savingState) ? "not-allowed" : "pointer",
              marginLeft: "auto",
            }}
          >
            {savingState ? "…" : "Save"}
          </button>
          {isManual && (
            <button
              onClick={handleRemoveManual}
              disabled={savingState}
              title="Remove this manual adjustment"
              style={{ ...btnStyle("danger"), padding: "3px 10px", fontSize: 11 }}
            >
              Remove
            </button>
          )}
          {!isManual && (onExcludeAll || onExcludeEmployee) && (
            showExclude ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
                Exclude:
                {onExcludeEmployee && (
                  <button
                    onClick={() => { setShowExclude(false); onExcludeEmployee(bonus.jobNum, bonus.techFirst, bonus.role); }}
                    title={`Remove this job from ${bonus.techFirst}'s bonuses only`}
                    style={{ ...btnStyle("danger"), padding: "3px 8px", fontSize: 11 }}
                  >
                    This employee
                  </button>
                )}
                {onExcludeAll && (
                  <button
                    onClick={() => { setShowExclude(false); onExcludeAll(bonus.jobNum); }}
                    title="Remove this job from everyone's bonuses + the leaderboard"
                    style={{ ...btnStyle("danger"), padding: "3px 8px", fontSize: 11 }}
                  >
                    Everyone
                  </button>
                )}
                <button onClick={() => setShowExclude(false)} style={{ ...btnStyle("secondary"), padding: "3px 8px", fontSize: 11 }}>Cancel</button>
              </span>
            ) : (
              <button
                onClick={() => setShowExclude(true)}
                title="Exclude this job from bonuses"
                style={{ ...btnStyle("danger"), padding: "3px 10px", fontSize: 11 }}
              >
                Exclude
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── Manual job bonus / deduction picker (admin) ──────────────────────────────
// Lets an admin pick one of this employee's non-qualifying jobs and assign a
// custom dollar amount (negative = deduction) with a note. Writes a manual
// override that replaces the computed ($0) bonus for that (job, tech).
function AddJobAdjustment({ jobs, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState("");   // "jobNum|role"
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  if (!jobs.length) return null;

  async function save() {
    const n = Number(amt);
    if (!sel || isNaN(n) || n === 0) return;
    const job = jobs.find(j => `${j.jobNum}|${j.role}` === sel);
    if (!job) return;
    setSaving(true);
    try {
      await onUpdate(job.jobNum, job.techFirst, job.role, { manual_amount: n, notes: note || null });
      setOpen(false); setSel(""); setAmt(""); setNote("");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ ...btnStyle("secondary"), padding: "5px 12px", fontSize: 11, marginTop: 6, alignSelf: "flex-start" }}
      >
        + Manual job bonus / deduction
      </button>
    );
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, color: C.muted }}>
        Assign a manual bonus or deduction to one of this employee's non-qualifying jobs. Negative amount = deduction (e.g. callback).
      </div>
      <select value={sel} onChange={e => setSel(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
        <option value="">Select a job…</option>
        {jobs.map(j => (
          <option key={`${j.jobNum}|${j.role}`} value={`${j.jobNum}|${j.role}`}>
            #{j.jobNum} · {j.client || "—"} · ${Math.round(j.revenue).toLocaleString("en-CA", { maximumFractionDigits: 0 })} · {j.profitPct.toFixed(0)}% ({j.role})
          </option>
        ))}
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="number" step="1" placeholder="Amount ($)" value={amt} onChange={e => setAmt(e.target.value)} style={{ ...inputStyle, width: 130 }} />
        <input placeholder="Reason / note" value={note} onChange={e => setNote(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={save} disabled={saving || !sel || !amt} style={{ ...btnStyle("primary"), opacity: (saving || !sel || !amt) ? 0.5 : 1, cursor: (saving || !sel || !amt) ? "not-allowed" : "pointer" }}>
          {saving ? "Saving…" : "Add adjustment"}
        </button>
        <button onClick={() => { setOpen(false); setSel(""); setAmt(""); setNote(""); }} style={btnStyle("secondary")}>Cancel</button>
      </div>
    </div>
  );
}

// ── Excluded-jobs manager (admin) ────────────────────────────────────────────
// Lists jobs manually removed from profit bonuses + the leaderboard, with a
// restore button each and an input to exclude a job by number (handy for
// non-qualifying jobs that never show as a payable row).
function ExcludedJobsPanel({ excludedJobs, perEmployeeExclusions = [], onExclude, onRestore, onRestoreEmployee, busy }) {
  const [input, setInput] = useState("");
  const list = [...excludedJobs].sort((a, b) => Number(a) - Number(b));
  function addJob() {
    const jn = input.trim();
    if (!jn) return;
    onExclude(jn);
    setInput("");
  }
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <SectionLabel>Excluded jobs ({list.length})</SectionLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            placeholder="Job #"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") addJob(); }}
            style={{ ...inputStyle, width: 110, padding: "5px 8px", fontSize: 12 }}
          />
          <button
            onClick={addJob}
            disabled={busy || !input.trim()}
            style={{ ...btnStyle("secondary"), padding: "5px 12px", fontSize: 12, opacity: (busy || !input.trim()) ? 0.5 : 1, cursor: (busy || !input.trim()) ? "not-allowed" : "pointer" }}
          >
            Exclude
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, margin: "2px 0 10px" }}>
        Excluded jobs pay no bonuses and drop out of the leaderboard ranking. Applies to both the Bonus Tracker and the Crew Performance leaderboard.
      </div>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>No jobs excluded for everyone.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {list.map(jn => (
            <span key={jn} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: "3px 6px 3px 10px", fontSize: 12, color: C.text }}>
              #{jn}
              <button
                onClick={() => onRestore(jn)}
                disabled={busy}
                title="Restore this job to bonuses + leaderboard"
                style={{ ...btnStyle("secondary"), padding: "1px 8px", fontSize: 11, opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
              >
                Restore
              </button>
            </span>
          ))}
        </div>
      )}

      {perEmployeeExclusions.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", margin: "14px 0 8px" }}>
            Excluded for one employee ({perEmployeeExclusions.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {perEmployeeExclusions.map(x => (
              <span key={`${x.jobNum}|${x.techFirst}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: "3px 6px 3px 10px", fontSize: 12, color: C.text }}>
                #{x.jobNum} · {x.name}
                <button
                  onClick={() => onRestoreEmployee(x.jobNum, x.techFirst, x.role)}
                  disabled={busy}
                  title={`Restore #${x.jobNum} for ${x.name}`}
                  style={{ ...btnStyle("secondary"), padding: "1px 8px", fontSize: 11, opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                >
                  Restore
                </button>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Employee Card ─────────────────────────────────────────────────────────────
function EmployeeCard({ employeeName, record, autoDeductions, profitBonuses = [], reviewBonus = null, referralBonus = null, onEditReferralAmount, onUpdateBonusOverride, onExcludeJob, onExcludeEmployeeJob, isAutoTracked, periodLabel, isQuarterly, onSave, saving, readOnly = false, isAdmin = false, onRefresh, onSetFinalized }) {
  const defaultBase = BASE_500_EMPLOYEES.has(employeeName) ? 500 : 0;
  const [baseBonus, setBaseBonus] = useState(String(record?.base_bonus ?? defaultBase));
  const [customItems, setCustomItems] = useState(record?.custom_items || []);
  const [adminNotes, setAdminNotes] = useState(record?.admin_notes || "");
  const [quarterlyReview, setQuarterlyReview] = useState(record?.quarterly_review || "");
  const [addLabel, setAddLabel] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addType, setAddType] = useState("bonus");
  const [showAddForm, setShowAddForm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [deductionDrillDown, setDeductionDrillDown] = useState(null);
  const [finalizeBusy, setFinalizeBusy] = useState(false);

  // A finalized record is locked: no edits until an admin reopens it.
  const locked = !!record?.finalized;

  // Sync from DB record when it changes
  useEffect(() => {
    setBaseBonus(String(record?.base_bonus ?? defaultBase));
    setCustomItems(record?.custom_items || []);
    setAdminNotes(record?.admin_notes || "");
    setQuarterlyReview(record?.quarterly_review || "");
    setDirty(false);
  }, [record?.id]);

  const activeDeds = isAutoTracked ? autoDeductions : [];
  const totalAutoDeduct = activeDeds.reduce((s, d) => s + (Number(d.total) || 0), 0);

  const localRecord = useMemo(() => ({
    base_bonus: Number(baseBonus) || 0,
    auto_deductions: activeDeds,
    custom_items: customItems,
    admin_notes: adminNotes,
    quarterly_review: quarterlyReview,
  }), [baseBonus, activeDeds, customItems, adminNotes, quarterlyReview]);

  const finalBonus = calcFinalBonus(localRecord, profitBonuses, reviewBonus, referralBonus);
  const reviewBonusTotal = Number(reviewBonus?.total) || 0;
  const referralBonusTotal = Number(referralBonus?.total) || 0;
  // Rows that pay (qualifying with $, or any manual adjustment) vs. the
  // employee's non-qualifying jobs that are available for a manual adjustment.
  // Crew (read-only) never see a $0 manual row — a job corrected down to nothing
  // simply isn't on their card, same as any job that didn't qualify.
  const crewView = readOnly || !isAdmin;
  const payableBonusRows = profitBonuses.filter(b =>
    (b.manual && !(crewView && Math.abs(Number(b.bonus) || 0) <= 0.005)) ||
    (b.qualifies && Math.abs(Number(b.bonus) || 0) > 0.005));
  const adjustableJobs   = profitBonuses.filter(b => !b.manual && !b.qualifies);
  // "Payable now" = client paid us (or manual) AND we haven't paid the employee yet
  const profitBonusPayable = profitBonuses.filter(isProfitBonusPayable).reduce((s, b) => s + (Number(b.bonus) || 0), 0);
  // "Awaiting client payment" = bonus accrued on a qualifying job but the client
  // hasn't paid yet (rows released via the pay-anyway override count as payable
  // instead). A corrected amount on a qualifying job waits the same way
  // (isProfitBonusPayable), so it counts here too.
  const profitBonusAwaiting = profitBonuses
    .filter(b => !b.paid && b.qualifies && (!b.manual || Number(b.bonus) > 0) && b.clientPaid === false && !b.payAnyway)
    .reduce((s, b) => s + (Number(b.bonus) || 0), 0);
  // Already paid out to employee
  const profitBonusPaid = profitBonuses.filter(b => b.paid).reduce((s, b) => s + (Number(b.bonus) || 0), 0);

  function addCustomItem() {
    const amt = parseFloat(addAmount);
    if (!addLabel.trim() || isNaN(amt)) return;
    const item = { label: addLabel.trim(), amount: Math.abs(amt), type: addType };
    setCustomItems(prev => [...prev, item]);
    setAddLabel(""); setAddAmount(""); setAddType("bonus");
    setShowAddForm(false);
    setDirty(true);
  }

  function removeCustomItem(idx) {
    setCustomItems(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  }

  function handleSave() {
    onSave(employeeName, {
      base_bonus: Number(baseBonus) || 0,
      auto_deductions: activeDeds,
      custom_items: customItems,
      admin_notes: adminNotes,
      quarterly_review: quarterlyReview,
      final_bonus: finalBonus,
    });
    setDirty(false);
  }

  // Finalize locks the saved bonus so it's included in the next Send-to-Cindy.
  // Reopen unlocks it (with a warning if it was already sent to payroll).
  async function handleFinalize() {
    if (!record?.id || !onSetFinalized) return;
    if (!window.confirm(`Finalize ${employeeName}'s ${periodLabel} bonus of $${finalBonus.toLocaleString("en-CA", { maximumFractionDigits: 0 })}? This locks it from edits until you reopen it. Finalized bonuses are the ones included when you Send to Cindy.`)) return;
    setFinalizeBusy(true);
    try { await onSetFinalized(record, true); }
    finally { setFinalizeBusy(false); }
  }

  async function handleReopen() {
    if (!record?.id || !onSetFinalized) return;
    const warn = record?.sent_to_cindy_at
      ? `This bonus was already SENT TO CINDY on ${new Date(record.sent_to_cindy_at).toLocaleDateString("en-CA")}. Reopening lets you edit it, but you'll then need to re-finalize and Send to Cindy again so she has the corrected amount. Continue?`
      : `Reopen ${employeeName}'s ${periodLabel} bonus for editing?`;
    if (!window.confirm(warn)) return;
    setFinalizeBusy(true);
    try { await onSetFinalized(record, false); }
    finally { setFinalizeBusy(false); }
  }

  const isSaving = saving === employeeName;

  return (
    <div style={{
      background: C.surface, border: `1px solid ${dirty ? C.accent + "60" : C.border}`,
      borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 16,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", color: C.text }}>
            {employeeName}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{periodLabel}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: finalBonus >= 0 ? C.green : C.red }}>
            ${finalBonus.toLocaleString("en-CA", { maximumFractionDigits: 0 })}
          </div>
          <div style={{ fontSize: 10, color: C.muted }}>Final Bonus</div>
          {locked && (
            <div style={{ marginTop: 6 }}>
              <Pill color={record?.sent_to_cindy_at ? C.accent : C.blue}>
                {record?.sent_to_cindy_at ? "Sent to Cindy" : "Finalized"}
              </Pill>
            </div>
          )}
        </div>
      </div>

      {/* Base Bonus */}
      <div>
        <SectionLabel>Base Bonus</SectionLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: C.muted, fontSize: 13 }}>$</span>
          <input
            type="number" value={baseBonus} min="0" step="50"
            disabled={locked}
            onChange={e => { setBaseBonus(e.target.value); setDirty(true); }}
            style={{ ...inputStyle, width: 120, opacity: locked ? 0.6 : 1, cursor: locked ? "not-allowed" : "text" }}
          />
        </div>
      </div>

      {/* Auto Deductions */}
      {isAutoTracked && (
        <div>
          <SectionLabel>Auto Deductions (from Jobber data)</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {activeDeds.map((d) => (
              <div
                key={d.metric}
                onClick={() => d.misses > 0 && setDeductionDrillDown(d)}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: d.total > 0 ? C.redDim : `${C.border}15`,
                  borderRadius: 8, padding: "8px 12px",
                  cursor: d.misses > 0 ? "pointer" : "default",
                  border: `1px solid ${d.misses > 0 ? C.red + "30" : "transparent"}`,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {d.misses} miss{d.misses !== 1 ? "es" : ""} × ${d.per_miss}
                    {d.total >= d.cap && <span style={{ color: C.yellow }}> (capped at ${d.cap})</span>}
                    {d.total_entries > 0 && <span> · {d.total_entries} total entries</span>}
                    {d.misses > 0 && <span style={{ color: C.accent }}> · tap to see details</span>}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: d.total > 0 ? C.red : C.muted, fontSize: 13 }}>
                  {d.total > 0 ? `-$${d.total}` : "$0"}
                </div>
              </div>
            ))}
            {totalAutoDeduct > 0 && (
              <div style={{ textAlign: "right", fontSize: 12, color: C.red, fontWeight: 600, paddingRight: 4 }}>
                Total auto deductions: -${totalAutoDeduct}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profit Bonuses (qualifying jobs + manual adjustments in this period) */}
      {(payableBonusRows.length > 0 || (isAdmin && !readOnly && adjustableJobs.length > 0)) && (
        <div>
          <SectionLabel>Profit Bonuses</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {payableBonusRows.map(pb => (
              <ProfitBonusRow
                key={`${pb.jobNum}|${pb.role}`}
                bonus={pb}
                readOnly={readOnly || !isAdmin}
                onUpdate={onUpdateBonusOverride}
                onExcludeAll={(isAdmin && !readOnly && onExcludeJob) ? onExcludeJob : undefined}
                onExcludeEmployee={(isAdmin && !readOnly && onExcludeEmployeeJob) ? onExcludeEmployeeJob : undefined}
              />
            ))}
            {payableBonusRows.length > 0 && (
              <div style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 12, paddingRight: 4, marginTop: 2,
              }}>
                <span style={{ color: C.muted }}>
                  {profitBonusPaid > 0 && (
                    <>Paid out: <span style={{ color: C.muted }}>${profitBonusPaid.toFixed(0)}</span> · </>
                  )}
                  {profitBonusAwaiting > 0 && (
                    <>Awaiting client: <span style={{ color: C.yellow }}>${profitBonusAwaiting.toFixed(0)}</span> · </>
                  )}
                  Ready to push
                </span>
                <span style={{ color: C.green, fontWeight: 700 }}>+${profitBonusPayable.toFixed(0)}</span>
              </div>
            )}
            {isAdmin && !readOnly && (
              <AddJobAdjustment jobs={adjustableJobs} onUpdate={onUpdateBonusOverride} />
            )}
          </div>
        </div>
      )}

      {/* Review Bonus */}
      {reviewBonusTotal > 0 && (
        <div style={{ marginTop: 14 }}>
          <SectionLabel>
            Review Bonus · ${reviewBonus.rate.base}/5★ + ${reviewBonus.rate.mention}/mention · cap ${reviewBonus.rate.monthly_cap}/mo
          </SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(() => {
              // Group rows by review id so a tech who earned BOTH base + mention
              // for the same review shows as one row with the combined amount.
              const byReview = new Map();
              for (const row of reviewBonus.rows) {
                const existing = byReview.get(row.reviewId);
                if (existing) {
                  existing.kinds.add(row.kind);
                  existing.amount += row.amount;
                } else {
                  byReview.set(row.reviewId, {
                    reviewId: row.reviewId,
                    date:     row.date,
                    reviewer: row.reviewer,
                    rating:   row.rating,
                    comment:  row.comment,
                    kinds:    new Set([row.kind]),
                    amount:   row.amount,
                  });
                }
              }
              return [...byReview.values()].sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
              );
            })().map((row) => {
              const dateStr = new Date(row.date).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
              const kindLabel = row.kinds.has("base") && row.kinds.has("mention")
                ? "5★ + mention"
                : row.kinds.has("mention") ? "mention only" : "5★";
              return (
                <div key={row.reviewId} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: C.greenDim, borderRadius: 8, padding: "6px 10px",
                  fontSize: 12, minWidth: 0,
                }}>
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, overflow: "hidden" }}>
                    <div style={{ color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <span style={{ color: C.yellow }}>{"★".repeat(row.rating)}</span>{" "}
                      <span>{row.reviewer || "(anon)"}</span>{" "}
                      <span style={{ color: C.muted }}>· {dateStr}</span>
                    </div>
                    <div style={{ color: C.muted, fontSize: 10 }}>{kindLabel}</div>
                  </div>
                  <span style={{ color: C.green, fontWeight: 700, marginLeft: 8 }}>
                    +${row.amount}
                  </span>
                </div>
              );
            })}
            {reviewBonus.cappedMonths > 0 && (
              <div style={{
                fontSize: 11, color: C.yellow, fontStyle: "italic",
                marginTop: 4, padding: "4px 8px",
              }}>
                {reviewBonus.totalUncapped > reviewBonus.total
                  ? `Capped at $${reviewBonus.rate.monthly_cap}/mo — earned $${reviewBonus.totalUncapped} raw, paying $${reviewBonus.total}`
                  : null}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Items */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <SectionLabel>Custom Items</SectionLabel>
          {!showAddForm && !locked && (
            <button onClick={() => setShowAddForm(true)} style={{ ...btnStyle("secondary"), padding: "4px 10px", fontSize: 11 }}>
              + Add Item
            </button>
          )}
        </div>

        {/* Add item form */}
        {showAddForm && (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
            padding: 12, marginBottom: 10, display: "flex", flexDirection: "column", gap: 8,
          }}>
            <input
              placeholder="Label (e.g. Safety milestone, Missed meeting)"
              value={addLabel}
              onChange={e => setAddLabel(e.target.value)}
              style={inputStyle}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="number" placeholder="Amount ($)" value={addAmount}
                onChange={e => setAddAmount(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
                min="0" step="10"
              />
              <select value={addType} onChange={e => setAddType(e.target.value)}
                style={{ ...inputStyle, flex: 1, cursor: "pointer" }}>
                <option value="bonus">Bonus (+)</option>
                <option value="deduction">Deduction (-)</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addCustomItem} style={btnStyle("primary")}>Add</button>
              <button onClick={() => { setShowAddForm(false); setAddLabel(""); setAddAmount(""); setAddType("bonus"); }} style={btnStyle("secondary")}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Custom item list */}
        {customItems.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {customItems.map((item, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: item.type === "bonus" ? C.greenDim : C.redDim,
                borderRadius: 8, padding: "7px 12px",
                minWidth: 0, overflow: "hidden",
              }}>
                <div style={{
                  fontSize: 12, color: C.text,
                  wordBreak: "break-all", overflowWrap: "anywhere", overflow: "hidden", maxWidth: "100%",
                }}>{item.label}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: item.type === "bonus" ? C.green : C.red }}>
                    {item.type === "bonus" ? "+" : "-"}${Number(item.amount).toLocaleString("en-CA", { maximumFractionDigits: 0 })}
                  </span>
                  {!locked && (
                    <button onClick={() => removeCustomItem(i)} style={{ ...btnStyle("danger"), padding: "2px 7px", fontSize: 11 }}>×</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>No custom items</div>
        )}
      </div>

      {/* Referral Bonuses — leaderboard engine rows (bill-month), payable in
          this period. Amount editable by admins; edits write the same
          referral_payout_overrides row the leaderboard's ✎ does. */}
      {referralBonusTotal !== 0 && (
        <div>
          <SectionLabel>Referral Bonus · pays in the month the job bills</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(referralBonus?.rows || []).map((r) => (
              <div key={`${r.jobNum}|${r.employee}`} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: C.greenDim, borderRadius: 8, padding: "6px 10px",
                fontSize: 12, minWidth: 0, gap: 8,
              }}>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, overflow: "hidden" }}>
                  <div style={{ color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.clientName || "(client)"} <span style={{ color: C.muted }}>· #{r.jobNum}</span>
                    {r.jobTitle ? <span style={{ color: C.dim }}> — {r.jobTitle}</span> : null}
                  </div>
                  <div style={{ color: C.muted, fontSize: 10 }}>
                    billed {r.billedMonth}
                    {r.carried ? " · carried forward" : ""}
                    {r.overridden ? " · edited" : ""}
                    {r.paid ? " · ✓ paid" : ""}
                  </div>
                </div>
                <span style={{ color: C.green, fontWeight: 700, whiteSpace: "nowrap" }}>
                  +${Number(r.amount || 0).toFixed(2)}
                </span>
                {isAdmin && !locked && onEditReferralAmount && (
                  <button
                    onClick={() => onEditReferralAmount(r)}
                    title="Set a custom amount for this referral"
                    style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 12, padding: 0 }}
                  >
                    ✎
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Notes — visible to owner/admin only */}
      {isAdmin && (
        <div>
          <SectionLabel>Admin Notes</SectionLabel>
          <textarea
            rows={3} placeholder="Monthly observations, context, notes for record..."
            value={adminNotes}
            disabled={locked}
            onChange={e => { setAdminNotes(e.target.value); setDirty(true); }}
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5, opacity: locked ? 0.6 : 1, cursor: locked ? "not-allowed" : "text" }}
          />
        </div>
      )}

      {/* Quarterly Review (quarterly view only) */}
      {isQuarterly && (
        <div>
          <SectionLabel>Quarterly Review</SectionLabel>
          <textarea
            rows={4} placeholder="Overall quarterly performance review and assessment..."
            value={quarterlyReview}
            onChange={e => { setQuarterlyReview(e.target.value); setDirty(true); }}
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>
      )}

      {/* Bonus breakdown summary */}
      <div style={{
        background: C.card, borderRadius: 10, padding: "10px 14px",
        fontSize: 12, display: "flex", flexDirection: "column", gap: 4,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: C.muted }}>Base</span>
          <span>${Number(baseBonus || 0).toLocaleString("en-CA", { maximumFractionDigits: 0 })}</span>
        </div>
        {isAutoTracked && totalAutoDeduct > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: C.muted }}>Auto deductions</span>
            <span style={{ color: C.red }}>-${totalAutoDeduct}</span>
          </div>
        )}
        {(profitBonusPayable + profitBonusPaid) > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: C.muted }}>
              Profit bonuses ({profitBonuses.filter(b => isProfitBonusPayable(b) || b.paid).length})
              {profitBonusPaid > 0 && profitBonusPayable === 0 ? " · paid out" : ""}
            </span>
            <span style={{ color: C.green }}>+${(profitBonusPayable + profitBonusPaid).toFixed(0)}</span>
          </div>
        )}
        {reviewBonusTotal > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: C.muted }}>
              Review bonus ({reviewBonus.rows.filter(r => r.kind === "base").length}× 5★
              {reviewBonus.rows.filter(r => r.kind === "mention").length > 0
                ? ` · ${reviewBonus.rows.filter(r => r.kind === "mention").length}× mention`
                : ""}
              {reviewBonus.cappedMonths > 0 ? ` · capped ${reviewBonus.cappedMonths}mo @ $${reviewBonus.rate.monthly_cap}` : ""})
            </span>
            <span style={{ color: C.green }}>+${reviewBonusTotal.toFixed(0)}</span>
          </div>
        )}
        {referralBonusTotal !== 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: C.muted }}>Referral bonus ({(referralBonus?.rows || []).length})</span>
            <span style={{ color: C.green }}>+${referralBonusTotal.toFixed(2)}</span>
          </div>
        )}
        {customItems.filter(i => i.type === "bonus").length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: C.muted }}>Custom bonuses</span>
            <span style={{ color: C.green }}>
              +${customItems.filter(i => i.type === "bonus").reduce((s, i) => s + Number(i.amount), 0).toLocaleString("en-CA", { maximumFractionDigits: 0 })}
            </span>
          </div>
        )}
        {customItems.filter(i => i.type === "deduction").length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: C.muted }}>Custom deductions</span>
            <span style={{ color: C.red }}>
              -${customItems.filter(i => i.type === "deduction").reduce((s, i) => s + Number(i.amount), 0).toLocaleString("en-CA", { maximumFractionDigits: 0 })}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 6, marginTop: 2, fontWeight: 700 }}>
          <span>Final</span>
          <span style={{ color: finalBonus >= 0 ? C.green : C.red }}>${finalBonus.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</span>
        </div>
      </div>

      {/* Save button — hidden in read-only mode and when finalized (locked) */}
      {!readOnly && !locked && (
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            ...btnStyle("primary"),
            opacity: isSaving ? 0.6 : 1,
            cursor: isSaving ? "not-allowed" : "pointer",
            alignSelf: "flex-end",
            padding: "9px 22px",
            fontSize: 13,
          }}
        >
          {isSaving ? "Saving…" : record?.id ? "Update" : "Save"}
        </button>
      )}
      {readOnly && (
        <div style={{
          alignSelf: "flex-end", fontSize: 11, color: C.muted,
          background: `${C.border}40`, padding: "6px 12px", borderRadius: 6
        }}>
          Read-only view
        </div>
      )}

      {/* Finalize / Reopen — admin only, record must be saved. Finalizing locks
          the bonus and includes it in the next "Send to Cindy" batch. */}
      {isAdmin && !readOnly && record?.id && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignSelf: "flex-end", alignItems: "flex-end" }}>
          {!locked ? (
            <button
              onClick={handleFinalize}
              disabled={finalizeBusy || dirty}
              title={dirty ? "Save changes first" : `Finalize and lock $${finalBonus.toLocaleString("en-CA", { maximumFractionDigits: 0 })}`}
              style={{
                ...btnStyle("secondary"),
                opacity: (finalizeBusy || dirty) ? 0.5 : 1,
                cursor: (finalizeBusy || dirty) ? "not-allowed" : "pointer",
                border: `1px solid ${C.green}50`,
                color: C.green,
              }}
            >
              {finalizeBusy ? "Finalizing…" : "Finalize"}
            </button>
          ) : (
            <>
              <div style={{
                fontSize: 11, color: record?.sent_to_cindy_at ? C.accent : C.blue,
              }}>
                {record?.sent_to_cindy_at
                  ? `Sent to Cindy ${new Date(record.sent_to_cindy_at).toLocaleDateString("en-CA")}`
                  : "Finalized — locked"}
              </div>
              <button
                onClick={handleReopen}
                disabled={finalizeBusy}
                style={{
                  ...btnStyle("secondary"),
                  opacity: finalizeBusy ? 0.5 : 1,
                  cursor: finalizeBusy ? "not-allowed" : "pointer",
                  border: `1px solid ${C.yellow}50`,
                  color: C.yellow,
                }}
              >
                {finalizeBusy ? "Reopening…" : "Reopen"}
              </button>
            </>
          )}
        </div>
      )}

      {/* Auto deduction drill-down modal */}
      {deductionDrillDown && createPortal(
        <div
          onClick={() => setDeductionDrillDown(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
            zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
              padding: 20, width: "100%", maxWidth: 520, maxHeight: "80vh",
              display: "flex", flexDirection: "column", gap: 14, overflowY: "auto",
            }}
          >
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.red }}>{deductionDrillDown.label}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {deductionDrillDown.misses} miss{deductionDrillDown.misses !== 1 ? "es" : ""} · -${deductionDrillDown.total} deducted
                  {deductionDrillDown.total >= deductionDrillDown.cap && ` (capped at $${deductionDrillDown.cap})`}
                </div>
              </div>
              <button
                onClick={() => setDeductionDrillDown(null)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: C.muted, padding: "0 4px", lineHeight: 1 }}
              >×</button>
            </div>

            {/* Items list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {(deductionDrillDown.items || []).length === 0 ? (
                <div style={{ fontSize: 13, color: C.dim, fontStyle: "italic" }}>No detail available</div>
              ) : deductionDrillDown.metric === "timesheet_rate" ? (
                <>
                  <div style={{ display: "flex", gap: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    <span style={{ flex: 1 }}>Shift Date</span>
                    <span style={{ flex: 1 }}>Submitted</span>
                    <span style={{ minWidth: 60, textAlign: "right" }}>Days Late</span>
                  </div>
                  {deductionDrillDown.items.map((item, i) => {
                    const daysLate = item.date && item.submitted
                      ? Math.round((new Date(item.submitted) - new Date(item.date)) / 86400000)
                      : null;
                    return (
                      <div key={i} style={{
                        display: "flex", gap: 8, padding: "7px 10px", borderRadius: 7,
                        background: C.redDim, fontSize: 13,
                      }}>
                        <span style={{ flex: 1, color: C.text }}>{item.date}</span>
                        <span style={{ flex: 1, color: C.muted }}>{item.submitted}</span>
                        <span style={{ minWidth: 60, textAlign: "right", color: C.red, fontWeight: 700 }}>
                          +{daysLate ?? "?"} day{daysLate !== 1 ? "s" : ""}
                        </span>
                      </div>
                    );
                  })}
                </>
              ) : deductionDrillDown.metric === "note_rate" ? (
                <>
                  <div style={{ display: "flex", gap: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    <span style={{ flex: 1 }}>Job #</span>
                    <span style={{ flex: 1 }}>Visit Date</span>
                  </div>
                  {deductionDrillDown.items.map((item, i) => (
                    <div key={i} style={{
                      display: "flex", gap: 8, padding: "7px 10px", borderRadius: 7,
                      background: C.redDim, fontSize: 13,
                    }}>
                      <span style={{ flex: 1, color: C.text }}>#{item.jobNumber}</span>
                      <span style={{ flex: 1, color: C.muted }}>{item.date}</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    <span style={{ flex: 1 }}>Job #</span>
                    <span style={{ flex: 1 }}>Scheduled</span>
                    <span style={{ flex: 1 }}>Completed</span>
                  </div>
                  {deductionDrillDown.items.map((item, i) => (
                    <div key={i} style={{
                      display: "flex", gap: 8, padding: "7px 10px", borderRadius: 7,
                      background: C.redDim, fontSize: 13,
                    }}>
                      <span style={{ flex: 1, color: C.text }}>#{item.jobNumber}</span>
                      <span style={{ flex: 1, color: C.muted }}>{item.scheduled}</span>
                      <span style={{ flex: 1, color: item.completed === "—" ? C.red : C.muted }}>{item.completed}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div style={{ textAlign: "right", fontSize: 12, color: C.muted }}>
              ${deductionDrillDown.per_miss}/miss · ${deductionDrillDown.total} deducted of ${deductionDrillDown.cap} cap
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Quarterly Summary ─────────────────────────────────────────────────────────
function QuarterlySummary({ year, quarter, monthlyRecords, employeeList = ACTIVE_EMPLOYEES_FALLBACK }) {
  // monthlyRecords: array of bonus_tracker rows for all 3 months in the quarter
  // employeeList: dynamic active-employees list from the parent so newly
  // discovered Jobber users show up in the roll-up without a deploy.
  const months = quarterMonths(year, quarter);

  const employeeSummaries = useMemo(() => {
    return employeeList.map(emp => {
      const empRecords = months.map(m => {
        const ps = toISODate(monthStart(year, m));
        return monthlyRecords.find(r => r.employee_name === emp && r.period_start === ps) || null;
      });
      const monthlyBonuses = empRecords.map(r => r?.final_bonus ?? null);
      // "sent" = pushed to payroll via Send-to-Cindy (i.e. paid out).
      const monthlySent = empRecords.map(r => !!r?.sent_to_cindy_at);
      const total = monthlyBonuses.filter(b => b !== null).reduce((s, b) => s + Number(b), 0);
      const paid  = empRecords.reduce((s, r) => s + (r?.sent_to_cindy_at ? Number(r.final_bonus) || 0 : 0), 0);
      const notes = empRecords.map((r, i) => r?.admin_notes ? `${MONTH_NAMES[months[i]]}: ${r.admin_notes}` : "").filter(Boolean);
      return { emp, monthlyBonuses, monthlySent, total, paid, notes };
    });
  }, [monthlyRecords, year, quarter, months, employeeList]);

  const grandTotal = employeeSummaries.reduce((s, e) => s + e.total, 0);
  const grandPaid  = employeeSummaries.reduce((s, e) => s + e.paid, 0);
  const recordedCount = employeeSummaries.filter(e => e.monthlyBonuses.some(b => b !== null)).length;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", marginBottom: 16, color: C.accent }}>
        Q{quarter + 1} {year} — Quarterly Roll-Up
      </div>

      {/* Grand total */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div style={{ background: C.card, borderRadius: 10, padding: "12px 18px" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Bonuses</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: C.accent }}>${grandTotal.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{recordedCount}/{employeeList.length} employees recorded</div>
        </div>
        <div style={{ background: C.card, borderRadius: 10, padding: "12px 18px" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Paid Out (Sent)</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: C.green }}>${grandPaid.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 11, color: C.muted }}>
            {grandTotal > 0 ? `${Math.round((grandPaid / grandTotal) * 100)}% of total · $${(grandTotal - grandPaid).toLocaleString("en-CA", { maximumFractionDigits: 0 })} pending` : "—"}
          </div>
        </div>
        {months.map((m, i) => {
          const monthTotal = employeeSummaries.reduce((s, e) => s + (Number(e.monthlyBonuses[i]) || 0), 0);
          const monthPaid  = employeeSummaries.reduce((s, e) => s + (e.monthlySent[i] ? Number(e.monthlyBonuses[i]) || 0 : 0), 0);
          return (
            <div key={m} style={{ background: C.card, borderRadius: 10, padding: "12px 18px" }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{MONTH_NAMES[m]}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>${monthTotal.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</div>
              <div style={{ fontSize: 11, color: monthPaid > 0 ? C.green : C.muted }}>
                {monthPaid > 0 ? `$${monthPaid.toLocaleString("en-CA", { maximumFractionDigits: 0 })} sent to payroll` : "none sent yet"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-employee table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 12px", color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${C.border}` }}>Employee</th>
              {months.map(m => (
                <th key={m} style={{ textAlign: "right", padding: "8px 12px", color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${C.border}` }}>
                  {MONTH_NAMES[m].slice(0, 3)}
                </th>
              ))}
              <th style={{ textAlign: "right", padding: "8px 12px", color: C.accent, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${C.border}` }}>Q Total</th>
            </tr>
          </thead>
          <tbody>
            {employeeSummaries.map(({ emp, monthlyBonuses, monthlySent, total }) => (
              <tr key={emp}>
                <td style={{ padding: "8px 12px", color: C.text, fontWeight: 600, borderBottom: `1px solid ${C.border}20` }}>{emp}</td>
                {monthlyBonuses.map((b, i) => (
                  <td key={i} style={{ padding: "8px 12px", textAlign: "right", color: b === null ? C.dim : monthlySent[i] ? C.green : C.text, borderBottom: `1px solid ${C.border}20` }}>
                    {b !== null ? `$${Number(b).toLocaleString("en-CA", { maximumFractionDigits: 0 })}${monthlySent[i] ? " ✓" : ""}` : "—"}
                  </td>
                ))}
                <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: total > 0 ? C.green : C.muted, borderBottom: `1px solid ${C.border}20` }}>
                  ${total.toLocaleString("en-CA", { maximumFractionDigits: 0 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
        <span style={{ color: C.green }}>Green ✓</span> = sent to Cindy / paid out via the monthly Send-to-Cindy report.
      </div>

      {/* Auto-generated notes summary */}
      {employeeSummaries.some(e => e.notes.length > 0) && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Monthly Notes Summary
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {employeeSummaries.filter(e => e.notes.length > 0).map(({ emp, notes }) => (
              <div key={emp} style={{ background: C.card, borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>{emp}</div>
                {notes.map((note, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{note}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Personal quarterly roll-up (crew without cross-employee access) ──────────
// Shown on the quarterly tab to non-admins so they still see their own three
// monthly finals + quarter total instead of a blank page.
function PersonalQuarterlySummary({ year, quarter, monthlyRecords, employeeName }) {
  const months = quarterMonths(year, quarter);
  const rows = months.map(m => {
    const ps = toISODate(monthStart(year, m));
    const rec = monthlyRecords.find(r => r.employee_name === employeeName && r.period_start === ps) || null;
    return { month: m, final: rec?.final_bonus ?? null, sent: !!rec?.sent_to_cindy_at };
  });
  const total = rows.reduce((s, r) => s + (Number(r.final) || 0), 0);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", marginBottom: 16, color: C.accent }}>
        Q{quarter + 1} {year} — Your Bonuses
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map(r => (
          <div key={r.month} style={{ display: "flex", justifyContent: "space-between", background: C.card, borderRadius: 8, padding: "10px 14px" }}>
            <span style={{ color: C.muted, fontSize: 13 }}>{MONTH_NAMES[r.month]}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: r.final === null ? C.dim : r.sent ? C.green : C.text }}>
              {r.final === null ? "—" : `$${Number(r.final).toLocaleString("en-CA", { maximumFractionDigits: 0 })}${r.sent ? " ✓" : ""}`}
            </span>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 2 }}>
          <span style={{ fontWeight: 700 }}>Quarter Total</span>
          <span style={{ fontWeight: 700, color: C.green }}>${total.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
        <span style={{ color: C.green }}>Green ✓</span> = sent to payroll.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

// Session-scoped cache of the last full data load — re-opening the Bonus
// Tracker paints instantly while a silent background refresh replaces it.
let bonusTrackerDataCache = null; // { raw, ts }

export default function BonusTracker() {
  const { session } = useSession();
  const userEmail = session?.user?.email?.toLowerCase() || "";
  const { isAdmin, hasPermission, jobberEmployeeName } = usePermissions();
  const canViewBonus = isAdmin || hasPermission("view_bonus");
  // Cross-employee visibility: only admins (auto-passes hasPermission) or
  // crew with the explicit "view_all_bonus_amounts" toggle can see other
  // people's bonus dollar amounts. Gates the quarterly roll-up table.
  const canSeeAllBonusAmounts = hasPermission("view_all_bonus_amounts");

  // ── Top-level sub-tab: performance bonuses vs referral bonuses ────────────
  const [bonusView, setBonusView] = useState("performance"); // 'performance' | 'referrals'

  // ── Referral payout data (NEW leaderboard engine — client-anchored) ────────
  // The card's referral money comes from the same engine as the Referral
  // Leaderboard (src/lib/referralPayout.js), NOT the legacy referral_bonuses
  // table — that table is stale (its sync has silently 400'd since 2026-04-22
  // on a missing column) and its month semantics disagree with the
  // bill-month rule. The legacy admin page stays on the Referrals sub-tab.
  const [referralTags, setReferralTags] = useState([]);
  const [referralRule, setReferralRule] = useState(null);
  const [referralOverrides, setReferralOverrides] = useState([]);
  const [jbInvoices, setJbInvoices] = useState([]);

  // ── Period state ────────────────────────────────────────────────────────────
  const [periodTab, setPeriodTab] = useState("monthly"); // 'monthly' | 'quarterly'
  const [selectedYear, setSelectedYear] = useState(currentYear());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter());

  // ── Jobber raw data ─────────────────────────────────────────────────────────
  const [timesheets, setTimesheets] = useState([]);
  const [visits, setVisits] = useState([]);
  const [notes, setNotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobberUsers, setJobberUsers] = useState([]); // [{id, name, is_active}]
  const [profitBonusOverrides, setProfitBonusOverrides] = useState({});
  const [profitBonusDefaultPct, setProfitBonusDefaultPct] = useState(PROFIT_BONUS_DEFAULT_PCT);
  const [profitBonusCrewPct, setProfitBonusCrewPct] = useState(PROFIT_BONUS_CREW_PCT);
  const [profitBonusRevMin, setProfitBonusRevMin] = useState(PROFIT_BONUS_REV_MIN);
  const [profitBonusMarginMin, setProfitBonusMarginMin] = useState(PROFIT_BONUS_PCT_MIN);
  const [reviews, setReviews] = useState([]);                       // rows from jobber_reviews
  const [reviewBonusBase, setReviewBonusBase]               = useState(REVIEW_BONUS_BASE_DEFAULT);
  const [reviewBonusMention, setReviewBonusMention]         = useState(REVIEW_BONUS_MENTION_DEFAULT);
  const [reviewBonusMonthlyCap, setReviewBonusMonthlyCap]   = useState(REVIEW_BONUS_MONTHLY_CAP_DEFAULT);
  const [excludedJobs, setExcludedJobs] = useState(() => new Set()); // job_nums removed from bonuses
  const [metricOverrides, setMetricOverrides] = useState([]); // manual excuse/add rows for deduction metrics
  const [excludingJob, setExcludingJob] = useState(false); // exclude/restore write in flight
  const [dataLoading, setDataLoading] = useState(true);
  // Set when the foreground load throws. Drives the "do not pay from this page"
  // banner — see the catch in loadJobberData().
  const [dataError, setDataError] = useState(null);
  // Toggle to include archived (is_active=false) Jobber employees in the bonus
  // tracker. Off by default — the leaderboards/bonus cards stay tight.
  const [showArchived, setShowArchived] = useState(false);

  // Source of truth for "is this employee currently active" is Jobber's
  // is_active flag. We intersect with the static ACTIVE_EMPLOYEES list so
  // base-bonus tiers, auto-tracking flags, etc. still apply by name. Anyone
  // archived in Jobber is hidden unless `showArchived` is on.
  // NOTE: must be declared AFTER the jobberUsers/showArchived useState calls
  // above — otherwise the useMemo body runs during first render and trips a
  // TDZ ReferenceError, blanking the entire page.
  const jobberActiveFirstNames = useMemo(() => {
    const set = new Set();
    for (const u of jobberUsers) {
      if (u?.is_active === false) continue;
      const f = firstName(u?.name || "");
      if (f) set.add(f);
    }
    return set;
  }, [jobberUsers]);

  const activeEmployees = useMemo(() => {
    // Until jobber_users loads, fall back to the full static list so the page
    // isn't blank during the first render.
    if (jobberUsers.length === 0) return ACTIVE_EMPLOYEES_FALLBACK;
    // Auto-discovery: anyone marked is_active=true in jobber_users whose first
    // name isn't already in the hardcoded order gets appended at the end. So
    // when a new apprentice is added in Jobber, they appear here without a
    // code deploy. (We still respect role-hierarchy ordering for the known
    // names by walking the fallback list first.)
    const knownFirsts = new Set(ACTIVE_EMPLOYEES_FALLBACK.map(e => firstName(e)));
    const newcomers = [];
    for (const u of jobberUsers) {
      if (u?.is_active === false) continue;
      const f = firstName(u?.name || "");
      if (!f || knownFirsts.has(f)) continue;
      knownFirsts.add(f);
      // Display name = capitalised first name to match the existing pattern.
      newcomers.push(f.charAt(0).toUpperCase() + f.slice(1));
    }
    const list = showArchived
      // showArchived = include people no longer active in Jobber (so historical
      // bonus records still appear). Combine fallback + newcomers, don't filter.
      ? [...ACTIVE_EMPLOYEES_FALLBACK, ...newcomers]
      // Hide anyone marked archived in Jobber, then append discovered newcomers.
      : [...ACTIVE_EMPLOYEES_FALLBACK.filter(e => jobberActiveFirstNames.has(firstName(e))), ...newcomers];
    // A mapped crew member must ALWAYS find their own row here — even if
    // jobber_users hasn't loaded yet (mobile) or doesn't list them, and even if
    // they aren't in the hardcoded fallback. Lorne (not in the fallback) saw
    // "Not Mapped Yet" on his phone for exactly this reason. Union in the
    // signed-in user's mapped name so their card + profit rows resolve.
    if (jobberEmployeeName && !list.some(e => firstName(e) === firstName(jobberEmployeeName))) {
      list.push(jobberEmployeeName);
    }
    return list;
  }, [jobberUsers, jobberActiveFirstNames, showArchived, jobberEmployeeName]);

  // Admins/owners see everyone; other crew see only their own mapped row.
  // A mapped non-admin ALWAYS sees their own row — never gate it on the roster
  // list (which may be mid-load on mobile, or may not contain them). Only a crew
  // member with NO jobber_employee_name gets the "Not Mapped Yet" screen below.
  const visibleEmployees = isAdmin
    ? activeEmployees
    : (jobberEmployeeName ? [jobberEmployeeName] : []);

  // ── Bonus records from DB ───────────────────────────────────────────────────
  // Monthly view: the selected month's records. Quarterly view: all 3 months
  // of the quarter (the roll-up sums them; per-employee quarterly cards were
  // removed because they never summed the monthly finals).
  const [bonusRecords, setBonusRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  // Month-starts (ISO dates) whose payroll batch has been sent to Cindy.
  // Drives the carry-forward: an unpaid bonus "lives" in the first UNSENT
  // month on/after its job's close month, so leftovers follow forward.
  const [sentMonths, setSentMonths] = useState(() => new Set());

  // ── Saving state ────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(null); // employee name being saved
  const [saveResult, setSaveResult] = useState(null); // { ok, msg }
  const [sendingReport, setSendingReport] = useState(false); // Send-to-Cindy in flight

  // ── Year options ────────────────────────────────────────────────────────────
  const yearOptions = useMemo(() => {
    const yr = currentYear();
    return [yr - 1, yr, yr + 1];
  }, []);

  // ── Period date range ───────────────────────────────────────────────────────
  const periodRange = useMemo(() => {
    if (periodTab === "monthly") {
      return { from: monthStart(selectedYear, selectedMonth), to: monthEnd(selectedYear, selectedMonth) };
    }
    return { from: quarterStart(selectedYear, selectedQuarter), to: quarterEnd(selectedYear, selectedQuarter) };
  }, [periodTab, selectedYear, selectedMonth, selectedQuarter]);

  // ── Load Jobber raw data (wide range — last 2 years, cached at module level) ──
  // Applies one full load to state; split out so a cached payload can hydrate
  // the page instantly on re-open while a silent background refresh runs.
  const applyLoadedData = useCallback((raw) => {
    const { tsAll, visitAll, notesAll, jobsAll, reviewsAll, metricOvAll, settingsRes, overridesRes, usersRes,
            refTagsRes, refRuleRes, refOvRes, invoicesAll } = raw;
    setTimesheets(tsAll || []);
    setVisits(visitAll || []);
    setNotes(notesAll || []);
    setJobs(jobsAll || []);
    setReviews(reviewsAll || []);
    setMetricOverrides(metricOvAll || []);
    setJobberUsers(usersRes?.data || []);
    setReferralTags(refTagsRes?.data || []);
    // No rule row = referral section simply doesn't render (same no-fallback
    // stance as the leaderboard — never invent a rate).
    setReferralRule(refRuleRes?.data?.[0] || null);
    setReferralOverrides(refOvRes?.data || []);
    setJbInvoices(invoicesAll || []);

    const settingsMap = {};
    for (const r of (settingsRes?.data || [])) {
      const v = r.value;
      const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : null;
      if (n != null && !isNaN(n)) settingsMap[r.key] = n;
    }
    const leadPct = settingsMap.profit_bonus_lead_pct ?? settingsMap.profit_bonus_default_pct;
    if (leadPct != null) setProfitBonusDefaultPct(leadPct);
    if (settingsMap.profit_bonus_crew_pct != null) setProfitBonusCrewPct(settingsMap.profit_bonus_crew_pct);
    // Qualifying thresholds — same app_settings keys the leaderboard reads.
    if (settingsMap.profit_bonus_revenue_threshold != null) setProfitBonusRevMin(settingsMap.profit_bonus_revenue_threshold);
    if (settingsMap.profit_bonus_margin_threshold != null) setProfitBonusMarginMin(settingsMap.profit_bonus_margin_threshold);
    if (settingsMap.review_bonus_base != null)         setReviewBonusBase(settingsMap.review_bonus_base);
    if (settingsMap.review_bonus_mention != null)      setReviewBonusMention(settingsMap.review_bonus_mention);
    if (settingsMap.review_bonus_monthly_cap != null)  setReviewBonusMonthlyCap(settingsMap.review_bonus_monthly_cap);
    // Manually-excluded jobs — JSONB array under one key.
    const exRow = (settingsRes?.data || []).find(r => r.key === "profit_bonus_excluded_jobs");
    setExcludedJobs(new Set((Array.isArray(exRow?.value) ? exRow.value : []).map(v => String(v))));

    // profit_bonus_overrides PK is (job_num, tech_first) — key the map the
    // same way the engine/leaderboard does so per-tech lead+crew overrides
    // (bonus_pct, weight, paid) resolve correctly.
    const ovMap = {};
    (overridesRes?.data || []).forEach(o => {
      if (o.job_num && o.tech_first) ovMap[`${o.job_num}|${o.tech_first}`] = o;
    });
    setProfitBonusOverrides(ovMap);
  }, []);

  const loadJobberData = useCallback(async (opts = {}) => {
    const background = !!opts.background;
    if (!background) setDataLoading(true);
    try {
      // Supabase REST silently caps each response at 1000 rows regardless of
      // .limit() — paginate to actually get everything. Page fetches run in
      // parallel batches (the sequential walk made this page take ~10s+).
      async function fetchAll(builder, pageSize = 1000, hardCap = 50000) {
        const first = await builder().range(0, pageSize - 1);
        if (first.error) throw first.error;
        let all = first.data || [];
        if (all.length < pageSize) return all;
        // Modest concurrency + one retry per page.
        // ⚠️ HISTORY: this burst has been blamed twice for "canceling statement due
        // to statement timeout", and both times the client was innocent. 2026-07-20
        // blamed "6 concurrent OFFSET sorts / missing sort indexes"
        // (20260720_pagination_sort_indexes.sql) — never re-measured, and wrong.
        // 2026-08-20 was a BARE STABLE+SECURITY DEFINER call in the RLS policy being
        // re-evaluated once per scanned row (4990 ms vs 16.5 ms wrapped) — see
        // security/buildRls.js. MEASURE THE SERVER before re-tuning BATCH.
        const BATCH = 3;
        for (let from = pageSize; all.length < hardCap; from += pageSize * BATCH) {
          const pages = await Promise.all(
            Array.from({ length: BATCH }, (_, i) =>
              builder().range(from + i * pageSize, from + (i + 1) * pageSize - 1)
            )
          );
          let done = false;
          for (let i = 0; i < pages.length; i++) {
            let p = pages[i];
            if (p.error) {
              p = await builder().range(from + i * pageSize, from + (i + 1) * pageSize - 1);
              if (p.error) throw p.error;
            }
            const rows = p.data || [];
            all = all.concat(rows);
            if (rows.length < pageSize) { done = true; break; }
          }
          if (done) break;
        }
        return all;
      }

      const [tsAll, visitAll, notesAll, jobsAll, reviewsAll, metricOvAll, settingsRes, overridesRes, usersRes,
             refTagsRes, refRuleRes, refOvRes, invoicesAll] = await Promise.all([
        fetchAll(() => supabase.from("jobber_timesheets").select("id,user_name,start_at,created_at,duration_hours,job_number,job_title").order("start_at", { ascending: false })),
        fetchAll(() => supabase.from("jobber_visits").select("id,job_number,start_at,completed_at,is_complete,assigned_users").order("start_at", { ascending: false })),
        fetchAll(() => supabase.from("jobber_job_notes").select("id,job_number,created_by_name,message,created_at,attachment_count").order("created_at", { ascending: false })),
        // client_id/status/invoice_status/quoted_total/created_date/created_at
        // feed the referral engine (buildReferralRows / billedMonthOf).
        fetchAll(() => supabase.from("jb_jobs").select("job_num,client_name,client_id,title,lead_tech,closed_date,total_revenue,profit,profit_pct,status,derived_status,invoice_status,amount_invoiced,amount_outstanding,quoted_total,created_date,created_at")),
        fetchAll(() => supabase.from("jobber_reviews").select("id,rating,created_at,comment,reviewer_name,auto_assigned_techs,assigned_techs,mentioned_techs,ignored,manual_mentions").order("created_at", { ascending: false })),
        // Manual excuse/add overrides — calcAutoDeductions honors them.
        fetchAll(() => supabase.from("metric_overrides").select("*").order("day", { ascending: false })),
        // Pull both keys: profit_bonus_lead_pct is what AdminDiagnostics writes today;
        // profit_bonus_default_pct is the legacy key we used to read. Prefer the
        // new key, fall back to the old one for installs that haven't been touched
        // since the rename. Also pulls the review-bonus keys so the tracker
        // mirrors whatever rate is set on the CrewPerformance leaderboard.
        supabase.from("app_settings").select("key,value").in("key", [
          "profit_bonus_lead_pct", "profit_bonus_default_pct", "profit_bonus_crew_pct",
          "profit_bonus_revenue_threshold", "profit_bonus_margin_threshold", "profit_bonus_excluded_jobs",
          "review_bonus_base", "review_bonus_mention", "review_bonus_monthly_cap",
        ]),
        supabase.from("profit_bonus_overrides").select("*"),
        supabase.from("jobber_users").select("id,name,is_active,role"),
        supabase.from("referral_clients").select("*"),
        supabase.from("referral_payout_settings").select("*"),
        supabase.from("referral_payout_overrides").select("*"),
        // Referral payout DATING only. Both tables are needed: jobber_invoices
        // carries Jobber's TRUE issuedDate, jb_invoices carries the job link in
        // job_nums (its job_number column is NULL on every row). The engine
        // takes the earliest date per job — see buildReferralRows for why
        // neither table alone dates a payout correctly.
        Promise.all([
          fetchAll(() => supabase.from("jobber_invoices").select("job_number,issued_date").order("issued_date", { ascending: false })),
          fetchAll(() => supabase.from("jb_invoices").select("job_nums,issued_date,created_at,due_date").order("created_at", { ascending: false })),
        ]).then(([jobberInv, jbInv]) => [...(jobberInv || []), ...(jbInv || [])]),
      ]);
      const raw = { tsAll, visitAll, notesAll, jobsAll, reviewsAll, metricOvAll, settingsRes, overridesRes, usersRes,
                    refTagsRes, refRuleRes, refOvRes, invoicesAll };
      bonusTrackerDataCache = { raw, ts: Date.now() };
      applyLoadedData(raw);
      setDataError(null);
    } catch (e) {
      // NOT non-fatal. Swallowing this renders empty auto-deductions, empty referral
      // rows and defaulted config — i.e. WRONG PAYROLL DOLLARS, silently, on the page
      // the month's bonuses are paid from. A background refresh can keep showing the
      // cached numbers, but a foreground failure has to say so.
      if (background) {
        console.warn("Bonus data background refresh failed:", e?.message || e);
      } else {
        console.error("Bonus data load failed:", e);
        setDataError(e?.message || String(e));
      }
    } finally {
      if (!background) setDataLoading(false);
    }
  }, [applyLoadedData]);

  useEffect(() => {
    if (!canViewBonus) return;
    // Stale-while-revalidate: paint instantly from the last load this session,
    // refresh silently behind it.
    if (bonusTrackerDataCache) {
      applyLoadedData(bonusTrackerDataCache.raw);
      setDataLoading(false);
      loadJobberData({ background: true });
    } else {
      loadJobberData();
    }
  }, [canViewBonus, loadJobberData, applyLoadedData]);

  // Combined refresh used by EmployeeCard after a QB push so the auto-mark
  // (profit_bonus_overrides.paid=true) and any bonus_tracker.qb_pushed_at
  // updates are reflected immediately.
  const refreshAll = useCallback(async () => {
    await loadJobberData();
    // bonus records depend on the selected period — load them last so the
    // newly-loaded jobs/overrides feed the per-card calc on next render.
    await loadBonusRecordsRef.current?.();
  }, [loadJobberData]);
  // Forward ref to loadBonusRecords (defined below). Avoids hoisting issues
  // since loadBonusRecords depends on period state declared after this point.
  const loadBonusRecordsRef = useRef(null);

  // ── Load bonus records for selected period ─────────────────────────────────
  const loadBonusRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      // Which months have been sent to Cindy — tiny table, fetch all. Refreshed
      // here (not loadJobberData) so a Send-to-Cindy updates the carry-forward
      // immediately.
      const { data: sentRows } = await supabase
        .from("bonus_tracker")
        .select("period_start")
        .eq("period_type", "monthly")
        .not("sent_to_cindy_at", "is", null);
      setSentMonths(new Set((sentRows || []).map(r => r.period_start)));

      if (periodTab === "monthly") {
        const ps = toISODate(monthStart(selectedYear, selectedMonth));
        const { data } = await supabase
          .from("bonus_tracker")
          .select("*")
          .eq("period_type", "monthly")
          .eq("period_start", ps);
        setBonusRecords(data || []);
      } else {
        // Quarterly view is a roll-up of the 3 monthly records.
        const months = quarterMonths(selectedYear, selectedQuarter);
        const monthStarts = months.map(m => toISODate(monthStart(selectedYear, m)));
        const { data } = await supabase
          .from("bonus_tracker")
          .select("*")
          .eq("period_type", "monthly")
          .in("period_start", monthStarts);
        setBonusRecords(data || []);
      }
    } catch (_) {
      // non-fatal
    } finally {
      setRecordsLoading(false);
    }
  }, [periodTab, selectedYear, selectedMonth, selectedQuarter]);

  useEffect(() => {
    if (canViewBonus) loadBonusRecords();
  }, [canViewBonus, loadBonusRecords]);

  // Keep refreshAll's pointer to loadBonusRecords current so the combined
  // refresh always invokes the latest closure (which captures current period).
  useEffect(() => { loadBonusRecordsRef.current = loadBonusRecords; }, [loadBonusRecords]);

  // ── Notes lookup ─────────────────────────────────────────────────────────────
  const notesLookup = useMemo(() => buildNotesLookup(notes), [notes]);

  // ── Referral payouts per employee for the selected period ──────────────────
  // Same engine + bill-month rule as the Referral Leaderboard, then the
  // tracker's own placement machinery on top:
  //   • paid rows pin to the batch month that paid them (paid_month stamp),
  //   • unpaid billed rows live in the first UNSENT month on/after their bill
  //     month (mirrors the profit-bonus carry-forward, so a referral billed in
  //     an already-sent month rolls forward instead of stranding),
  //   • unbilled rows aren't payable and stay off the card (the leaderboard
  //     shows them as carried).
  const referralBonusesMap = useMemo(() => {
    if (!referralRule) return {};
    const rows = buildReferralRows({
      referralClients: referralTags, jobs, invoices: jbInvoices,
      rule: referralRule, overrides: referralOverrides,
    });
    // Payroll floor: program_start (parsed with the engine's LOCAL-midnight
    // parser — new Date("YYYY-MM-DD") reads as the previous day in Pacific),
    // else a hardcoded July 2026 floor. The leaderboard deliberately DISPLAYS
    // retroactive history, but payroll must not auto-pay it: with no floor,
    // ~$830 of pre-program Kal Tire referrals (billed May/June, months whose
    // batches are already sent) would silently join the next batch.
    const floor = referralRule.program_start
      ? referralMonthKey(referralParseDate(referralRule.program_start))
      : "2026-07";
    const { from, to } = periodRange;
    const viewedISO = periodTab === "monthly" ? toISODate(monthStart(selectedYear, selectedMonth)) : null;
    const monthKeyToISO = (mk) => `${mk}-01`;
    const activeISOof = (billedMonth) => {
      let iso = monthKeyToISO(billedMonth);
      for (let i = 0; i < 48 && sentMonths.has(iso); i++) {
        // m from the ISO string is 1-based; monthStart() is 0-indexed, so
        // passing m unchanged IS the next month (Dec: new Date(y,12,1)
        // rolls to Jan y+1 natively). Passing m+1 here skipped a month
        // per hop (July→September) — caught in review.
        const [y, m] = iso.split("-").map(Number);
        iso = toISODate(monthStart(y, m));
      }
      return iso;
    };

    const map = {};
    for (const r of rows) {
      if (!r.billedMonth) continue; // unbilled — not payable yet
      if (floor && r.billedMonth < floor) continue;
      if (r.amount == null || Math.abs(r.amount) < 0.005) continue;
      let carried = false;
      if (viewedISO) {
        if (r.paid && r.paidMonth) {
          if (monthKeyToISO(r.paidMonth) !== viewedISO) continue;
          carried = r.paidMonth !== r.billedMonth;
        } else {
          const activeISO = activeISOof(r.billedMonth);
          if (activeISO !== viewedISO) continue;
          carried = activeISO !== monthKeyToISO(r.billedMonth);
        }
      } else {
        // Quarterly roll-up view: bucket purely by bill month.
        const billed = new Date(monthKeyToISO(r.billedMonth) + "T12:00:00");
        if (billed < from || billed > to) continue;
      }
      const empName = activeEmployees.find(e => firstName(e) === firstName(r.employee));
      if (!empName) continue;
      if (!map[empName]) map[empName] = { rows: [], total: 0 };
      map[empName].rows.push({ ...r, carried });
      map[empName].total += Number(r.amount) || 0;
    }
    Object.values(map).forEach(e => e.rows.sort((a, b) => String(b.billedMonth).localeCompare(String(a.billedMonth))));
    return map;
  }, [referralRule, referralTags, jobs, jbInvoices, referralOverrides, periodRange, periodTab, selectedYear, selectedMonth, sentMonths, activeEmployees]);

  // ── Edit one referral payout amount (admin) ────────────────────────────────
  // Same override row the leaderboard edits — the two surfaces always agree.
  const handleEditReferralAmount = useCallback(async (row) => {
    const current = row.overridden ? row.amount : row.computedAmount;
    const raw = window.prompt(
      `Custom referral amount for ${row.employee} — #${row.jobNum} ${row.clientName || ""}\n` +
      `Computed by the rule: ${row.computedAmount == null ? "—" : `$${row.computedAmount.toFixed(2)}`}\n` +
      `Leave blank to go back to the computed amount.`,
      current == null ? "" : String(Math.round(current * 100) / 100)
    );
    if (raw === null) return;
    const trimmed = raw.trim().replace(/^\$/, "");
    const custom = trimmed === "" ? null : Number(trimmed);
    if (custom !== null && !isFinite(custom)) return;
    const { error } = await supabase
      .from("referral_payout_overrides")
      .upsert({
        job_num: String(row.jobNum),
        employee_name: row.employee,
        custom_amount: custom,
        updated_at: new Date().toISOString(),
        updated_by: userEmail,
      }, { onConflict: "organization_id,job_num,employee_name" });
    if (error) {
      setSaveResult({ ok: false, msg: error.message });
      setTimeout(() => setSaveResult(null), 3500);
      return;
    }
    const { data } = await supabase.from("referral_payout_overrides").select("*");
    setReferralOverrides(data || []);
  }, [userEmail]);

  // ── Compute auto-deductions for auto-tracked employees (scoped to visible set) ──
  const autoDeductionsMap = useMemo(() => {
    const map = {};
    // Built once per timesheet load, not per tech — every tech's gate reads it.
    const hoursIndex = buildPaperworkHoursIndex(timesheets);
    AUTO_TRACKED.filter(emp => visibleEmployees.includes(emp)).forEach(emp => {
      map[emp] = calcAutoDeductions(emp, timesheets, visits, notesLookup, periodRange.from, periodRange.to, metricOverrides, hoursIndex);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheets, visits, notesLookup, periodRange, metricOverrides]);

  // ── Inputs for the shared profit-bonus engine ────────────────────────────
  const jobTechHoursMap = useMemo(() => buildJobTechHoursMap(timesheets), [timesheets]);
  const roleByFirst = useMemo(() => buildRoleByFirst(jobberUsers), [jobberUsers]);
  const profitBonusSettings = useMemo(() => ({
    lead_pct: profitBonusDefaultPct,
    crew_pct: profitBonusCrewPct,
    revenue_threshold: profitBonusRevMin,
    margin_threshold: profitBonusMarginMin,
  }), [profitBonusDefaultPct, profitBonusCrewPct, profitBonusRevMin, profitBonusMarginMin]);

  // ── Profit bonuses per employee for the selected period ──────────────────
  // Built from the SAME engine the CrewPerformance leaderboard uses, so lead
  // AND crew payouts always agree between the two surfaces. We keep ALL of the
  // employee's rows for the period (qualifying or not) — the card splits them
  // into payable bonuses vs. non-qualifying jobs available for a manual
  // adjustment. Rows excluded for this tech are already dropped by the engine.
  const profitBonusesMap = useMemo(() => {
    const { from, to } = periodRange;
    const rows = computeBonusRows({
      jobs,
      jobTechHoursMap,
      roleByFirst,
      overrides: profitBonusOverrides,
      settings: profitBonusSettings,
      excludedJobs,
    });

    // ── Carry-forward (monthly view only) ────────────────────────────────
    // An unpaid payable bonus "lives" in the first month on/after its job's
    // close month whose payroll batch has NOT been sent to Cindy — so bonuses
    // still awaiting the client (or just not paid out) follow forward month
    // after month instead of hiding in a past month's view. Once marked
    // "paid out" the row returns to its close month as history. Floor:
    // May 2026, the first month paid through Send-to-Cindy — older rows
    // predate this process and stay pinned to their close month.
    const CARRY_FLOOR = new Date(2026, 4, 1);
    const monthISO = (y, m) => toISODate(monthStart(y, m));
    const viewedISO = periodTab === "monthly" ? toISODate(from) : null;
    const activeMonthOf = (closedDate) => {
      let y = closedDate.getFullYear(), m = closedDate.getMonth();
      // Walk past every already-sent month (bounded — sentMonths is finite).
      for (let i = 0; i < 48 && sentMonths.has(monthISO(y, m)); i++) {
        m++; if (m > 11) { m = 0; y++; }
      }
      return monthISO(y, m);
    };

    const map = {};
    rows.forEach(r => {
      const inPeriod = r.closedDate >= from && r.closedDate <= to;
      let carried = false;
      // A $0 manual row (a zeroed-out false profit) pays nothing, so it stays in
      // its close month instead of carrying forward forever. It is NOT stamped
      // paid: if the $0 is later removed or changed, the real amount carries into
      // the next unsent batch like any other unpaid row.
      const payableShape = (r.manual || r.qualifies) && Math.abs(Number(r.bonus) || 0) > 0.005;
      const paidMonthISO = r.paidMonth ? String(r.paidMonth).slice(0, 10) : null;
      if (viewedISO && r.paid && paidMonthISO) {
        // Paid rows pin to the batch month that PAID them (Send-to-Cindy stamps
        // paid_month), not their close month — a pay-anyway row released in
        // June's batch stays in June even though the job closed earlier and
        // June is now "sent". This is the SNDL fix.
        if (paidMonthISO !== viewedISO) return;
        carried = paidMonthISO !== monthISO(r.closedDate.getFullYear(), r.closedDate.getMonth());
      } else if (viewedISO && payableShape && !r.paid && r.closedDate >= CARRY_FLOOR) {
        const originISO = monthISO(r.closedDate.getFullYear(), r.closedDate.getMonth());
        const activeISO = activeMonthOf(r.closedDate);
        if (activeISO !== viewedISO) return; // lives in another month's view
        carried = activeISO !== originISO;
      } else if (!inPeriod) {
        return; // paid-without-paid_month / non-qualifying / quarterly / pre-floor: close-month only
      }
      const empName = activeEmployees.find(e => firstName(e) === r.techFirst);
      if (!empName) return;

      // "Client paid us for this job" — pulled from the jb_jobs invoice rollup
      // (derived_status / amount_invoiced / amount_outstanding). We also accept
      // "outstanding ≈ 0 with something invoiced" as paid, since a freshly-paid
      // invoice can briefly show in either state during sync.
      const j = r.job;
      const ds = String(j.derived_status || "").toLowerCase();
      const invoiced = Number(j.amount_invoiced) || 0;
      const outstanding = Number(j.amount_outstanding) || 0;
      const clientPaid = ds === "paid" || (invoiced > 0 && outstanding < 0.01);

      if (!map[empName]) map[empName] = [];
      map[empName].push({
        jobNum: r.jobNum,
        techFirst: r.techFirst,
        role: r.role,                  // "lead" | "crew"
        client: r.clientName,
        title: r.jobTitle,
        closedDate: j.closed_date,
        revenue: r.revenue,
        profit: r.profit,
        profitPct: r.profitPct,
        excess: r.excess,
        bonusPct: r.bonus_pct,
        bonus: r.bonus,
        weight: r.weight,              // crew-only share (null for lead)
        qualifies: r.qualifies,
        manual: !!r.manual,            // admin manual $ adjustment in effect
        manualAmount: r.manualAmount ?? null,
        notes: r.notes ?? null,        // reason for the manual adjustment
        overridden: r.overridden,
        paid: r.paid,                  // "bonus has been paid out to employee"
        payAnyway: !!r.payAnyway,      // admin released it before the client paid
        clientPaid,                    // "client has paid us for the underlying job"
        paymentStatus: ds || "no_invoice",
        amountOutstanding: outstanding,
        carried,                       // rolled forward from an earlier (sent) month
        carriedFrom: carried
          ? `${MONTH_NAMES[r.closedDate.getMonth()].slice(0, 3)} ${r.closedDate.getFullYear()}`
          : null,
      });
    });
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => new Date(b.closedDate).getTime() - new Date(a.closedDate).getTime())
    );
    return map;
  }, [jobs, periodRange, periodTab, sentMonths, jobTechHoursMap, roleByFirst, profitBonusOverrides, profitBonusSettings, excludedJobs, activeEmployees]);

  // ── Review-bonus rows for selected period ──────────────────────────────────
  // Mirrors the Review Bonus tile on the CrewPerformance leaderboard. For each
  // 5★ review in the period:
  //   - Every credited tech (assigned_techs override wins over auto_assigned_techs)
  //     earns the base bonus
  //   - Every name mention in the comment earns the mention bonus
  //   - Per-month per-tech total is capped at monthly_cap
  // The shape per-employee mirrors profitBonusesMap so EmployeeCard can render
  // it the same way: an array of payable rows plus a summary on the entry.
  const reviewBonusesMap = useMemo(() => {
    const { from, to } = periodRange;
    const base    = reviewBonusBase;
    const mention = reviewBonusMention;
    const cap     = reviewBonusMonthlyCap;

    // Build per-employee per-month tally first, then cap and convert to rows.
    // tally[emp][monthKey] = { base: n, mention: n, rows: [...] }
    const tally = {};
    const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const addTally = (emp, mKey, kind, row) => {
      if (!tally[emp]) tally[emp] = {};
      if (!tally[emp][mKey]) tally[emp][mKey] = { base: 0, mention: 0, rows: [] };
      tally[emp][mKey][kind] += 1;
      tally[emp][mKey].rows.push(row);
    };

    for (const r of (reviews || [])) {
      if (r.rating !== 5) continue;
      if (r.ignored) continue; // admin marked "nobody was involved" — no credit
      const d = r.created_at ? new Date(r.created_at) : null;
      if (!d || isNaN(d.getTime())) continue;
      if (d < from || d > to) continue;
      const mKey = monthKey(d);

      // Credited techs — manual override wins. Track who earned the base so a
      // name-mention below can grant base to a tech the attribution missed.
      const baseEmps = new Set();
      // Manual assigned_techs pays whoever the human picked; the AUTO list
      // drops non-crew staff (Taylor/Mallory must never be auto-credited).
      const isManualCredit = Array.isArray(r.assigned_techs) && r.assigned_techs.length > 0;
      const credited = isManualCredit
        ? r.assigned_techs
        : (Array.isArray(r.auto_assigned_techs)
            ? r.auto_assigned_techs.filter(t => !NON_CREW_FIRST.includes(firstName(t)))
            : []);
      for (const fullName of credited) {
        const first = firstName(fullName);
        if (!first) continue;
        // Owner/family carve-outs never earn review bonuses. Office staff
        // (taylor) are auto-excluded via isBonusExcluded but DO pay on a
        // manual assignment — a deliberate human credit (standing rule).
        if (OWNER_EXCLUDED_FIRST.includes(first)) continue;
        if (!isManualCredit && isBonusExcluded(first)) continue;
        const empName = activeEmployees.find(e => firstName(e) === first);
        if (!empName) continue;
        baseEmps.add(empName);
        addTally(empName, mKey, "base", {
          reviewId:  r.id,
          date:      r.created_at,
          reviewer:  r.reviewer_name || "",
          rating:    r.rating,
          kind:      "base",
          amount:    base,
          comment:   r.comment || "",
        });
      }

      // Mentions — re-scan the comment text for each active employee's
      // first name (word-boundary), instead of trusting r.mentioned_techs.
      // The leaderboard's Name Mentions tile does the same; reading the
      // stored field misses cases where extractTechMentions wasn't run on
      // insert (e.g. the manual insert-review action before pippin was
      // added to the roster) or where a new tech joined after a review
      // came in. This way "Justin in particular" credits Justin even if
      // his name was never written to mentioned_techs.
      const lower = (r.comment || "").toLowerCase();
      // Manual mention credit (admin checked a guy the auto-scan missed) pays
      // exactly like a scanned mention — same matcher as CrewPerformance.
      const manualFirsts = new Set(
        (Array.isArray(r.manual_mentions) ? r.manual_mentions : []).map(n => firstName(n))
      );
      if (lower || manualFirsts.size > 0) {
        const seenMention = new Set();
        for (const empName of activeEmployees) {
          const fn = firstName(empName);
          if (!fn || seenMention.has(fn)) continue;
          // Owner/family carve-outs never pay. Office staff (taylor/mallory)
          // pay ONLY on a manual mention — the auto scan never credits them.
          if (OWNER_EXCLUDED_FIRST.includes(fn)) continue;
          if (!manualFirsts.has(fn) && (isBonusExcluded(fn) || NON_CREW_FIRST.includes(fn))) continue;
          // Word-boundary substring scan — same matcher CrewPerformance.jsx
          // uses for its mention metric. Avoids false positives like "morgan"
          // matching inside a longer word.
          let searchFrom = 0;
          let hit = manualFirsts.has(fn);
          while (!hit) {
            const idx = lower.indexOf(fn, searchFrom);
            if (idx === -1) break;
            const before = idx > 0 ? lower[idx - 1] : " ";
            const after = idx + fn.length < lower.length ? lower[idx + fn.length] : " ";
            if (!/[a-z]/.test(before) && !/[a-z]/.test(after)) { hit = true; break; }
            searchFrom = idx + fn.length;
          }
          if (!hit) continue;
          seenMention.add(fn);
          addTally(empName, mKey, "mention", {
            reviewId:  r.id,
            date:      r.created_at,
            reviewer:  r.reviewer_name || "",
            rating:    r.rating,
            kind:      "mention",
            amount:    mention,
            comment:   r.comment || "",
          });
          // A name mention proves the tech worked the job, so grant the base
          // too if the attribution (auto_assigned_techs) missed them.
          if (!baseEmps.has(empName)) {
            baseEmps.add(empName);
            addTally(empName, mKey, "base", {
              reviewId:  r.id,
              date:      r.created_at,
              reviewer:  r.reviewer_name || "",
              rating:    r.rating,
              kind:      "base",
              amount:    base,
              comment:   r.comment || "",
            });
          }
        }
      }
    }

    // Cap per-month per-employee and produce the final per-employee structure.
    const map = {};
    for (const emp of Object.keys(tally)) {
      const months = tally[emp];
      let totalUncapped = 0;
      let totalCapped   = 0;
      let cappedMonths  = 0;
      const allRows = [];
      const cappedMonthList = [];
      for (const mKey of Object.keys(months)) {
        const { base: bCount, mention: mCount, rows } = months[mKey];
        const monthRaw = bCount * base + mCount * mention;
        const monthCap = Math.min(monthRaw, cap);
        totalUncapped += monthRaw;
        totalCapped   += monthCap;
        if (monthRaw > cap) {
          cappedMonths++;
          cappedMonthList.push({ month: mKey, raw: monthRaw, capped: monthCap });
        }
        allRows.push(...rows);
      }
      allRows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      map[emp] = {
        rows:           allRows,
        total:          totalCapped,
        totalUncapped,
        cappedMonths,
        cappedMonthList,
        rate:           { base, mention, monthly_cap: cap },
      };
    }
    return map;
  }, [reviews, periodRange, reviewBonusBase, reviewBonusMention, reviewBonusMonthlyCap, activeEmployees]);

  // Per-(job, tech) exclusions in effect — drives a restore list in the
  // Excluded-jobs panel (engine drops these rows, so they can't be restored
  // from an employee card). Derived from the override map.
  const perEmployeeExclusions = useMemo(() => {
    return Object.values(profitBonusOverrides)
      .filter(o => o && o.excluded && o.job_num && o.tech_first)
      .map(o => ({
        jobNum: String(o.job_num),
        techFirst: String(o.tech_first),
        role: o.role || "crew",
        name: activeEmployees.find(e => firstName(e) === String(o.tech_first)) || String(o.tech_first),
      }))
      .sort((a, b) => Number(a.jobNum) - Number(b.jobNum));
  }, [profitBonusOverrides, activeEmployees]);

  // ── Save handler for a per-(job,tech) bonus override ──────────────────────
  // profit_bonus_overrides PK is (job_num, tech_first), with tech_first + role
  // NOT NULL. We always send both, and only the fields the row exposes
  // (bonus_pct for leads; paid for either). Columns omitted from the payload
  // are preserved by the upsert, so toggling "paid" won't clobber a crew weight.
  const handleUpdateBonusOverride = useCallback(async (jobNum, techFirst, role, fields = {}) => {
    if (!techFirst || !role) {
      setSaveResult({ ok: false, msg: "Missing tech/role for override" });
      setTimeout(() => setSaveResult(null), 3000);
      return;
    }
    const key = `${jobNum}|${techFirst}`;
    const payload = {
      job_num: String(jobNum),
      tech_first: String(techFirst),
      role,
      ...(fields.bonus_pct !== undefined ? { bonus_pct: fields.bonus_pct } : {}),
      ...(fields.weight !== undefined ? { weight: fields.weight } : {}),
      ...(fields.paid !== undefined ? { paid: !!fields.paid } : {}),
      // paid_month pins a paid row to the payroll month that paid it. Explicit
      // value wins; otherwise flipping paid ON stamps the month being VIEWED
      // (that's the batch it joins), and flipping paid OFF clears the pin.
      ...(fields.paid_month !== undefined
        ? { paid_month: fields.paid_month }
        : fields.paid === true && periodTab === "monthly"
          ? { paid_month: toISODate(monthStart(selectedYear, selectedMonth)) }
          : fields.paid === false
            ? { paid_month: null }
            : {}),
      ...(fields.pay_anyway !== undefined ? { pay_anyway: !!fields.pay_anyway } : {}),
      ...(fields.manual_amount !== undefined ? { manual_amount: fields.manual_amount } : {}),
      ...(fields.notes !== undefined ? { notes: fields.notes } : {}),
      ...(fields.excluded !== undefined ? { excluded: !!fields.excluded } : {}),
      updated_at: new Date().toISOString(),
      updated_by: userEmail,
    };
    const { error } = await supabase
      .from("profit_bonus_overrides")
      .upsert(payload, { onConflict: "job_num,tech_first" });
    if (error) {
      setSaveResult({ ok: false, msg: error.message || "Override save failed" });
      setTimeout(() => setSaveResult(null), 3000);
      return;
    }
    setProfitBonusOverrides(prev => ({ ...prev, [key]: { ...prev[key], ...payload } }));
    setSaveResult({ ok: true, msg: `Job #${jobNum} override saved` });
    setTimeout(() => setSaveResult(null), 2000);
  }, [userEmail, periodTab, selectedYear, selectedMonth]);

  // ── Exclude / restore a job from profit bonuses (and the leaderboard) ─────
  // Stored as a JSONB array in app_settings under "profit_bonus_excluded_jobs"
  // so this view and the CrewPerformance leaderboard (which reads the same key)
  // stay in sync. An excluded job yields no bonus rows and drops out of the
  // leaderboard ranking entirely.
  const toggleJobExcluded = useCallback(async (jobNum, exclude) => {
    const jn = String(jobNum).trim();
    if (!jn) return;
    setExcludingJob(true);
    try {
      const next = new Set(excludedJobs);
      if (exclude) next.add(jn); else next.delete(jn);
      const { error } = await supabase
        .from("app_settings")
        .upsert(
          { key: "profit_bonus_excluded_jobs", value: [...next], updated_at: new Date().toISOString(), updated_by: userEmail },
          { onConflict: "key" }
        );
      if (error) {
        setSaveResult({ ok: false, msg: error.message || "Could not update excluded jobs" });
        setTimeout(() => setSaveResult(null), 3000);
        return;
      }
      setExcludedJobs(next);
      setSaveResult({ ok: true, msg: exclude ? `Job #${jn} excluded from bonuses` : `Job #${jn} restored` });
      setTimeout(() => setSaveResult(null), 2500);
    } finally {
      setExcludingJob(false);
    }
  }, [excludedJobs, userEmail]);

  // ── Get DB record for an employee ──────────────────────────────────────────
  function getRecord(employeeName, records) {
    return records.find(r => r.employee_name === employeeName) || null;
  }

  // ── Save handler ───────────────────────────────────────────────────────────
  async function handleSave(employeeName, updates) {
    setSaving(employeeName);
    setSaveResult(null);
    try {
      let ps, pe, pType;
      if (periodTab === "monthly") {
        ps = toISODate(monthStart(selectedYear, selectedMonth));
        pe = toISODate(monthEnd(selectedYear, selectedMonth));
        pType = "monthly";
      } else {
        ps = toISODate(quarterStart(selectedYear, selectedQuarter));
        pe = toISODate(quarterEnd(selectedYear, selectedQuarter));
        pType = "quarterly";
      }

      const payload = {
        employee_name: employeeName,
        period_type: pType,
        period_start: ps,
        period_end: pe,
        ...updates,
        updated_at: new Date().toISOString(),
        created_by: userEmail,
      };

      const { error } = await supabase
        .from("bonus_tracker")
        .upsert(payload, { onConflict: "employee_name,period_type,period_start" });

      if (error) throw error;
      setSaveResult({ ok: true, msg: `${employeeName} saved` });
      await loadBonusRecords();
    } catch (e) {
      setSaveResult({ ok: false, msg: e.message || "Save failed" });
    } finally {
      setSaving(null);
      setTimeout(() => setSaveResult(null), 3000);
    }
  }

  // ── Finalize / reopen a single bonus (locks/unlocks the card) ───────────────
  async function handleSetFinalized(rec, finalized) {
    if (!rec?.id) return;
    setSaveResult(null);
    try {
      const patch = finalized
        ? { finalized: true,  finalized_at: new Date().toISOString(), finalized_by: userEmail }
        : { finalized: false, finalized_at: null, finalized_by: null };
      const { error } = await supabase.from("bonus_tracker").update(patch).eq("id", rec.id);
      if (error) throw error;
      setSaveResult({ ok: true, msg: `${rec.employee_name} ${finalized ? "finalized" : "reopened"}` });
      await loadBonusRecords();
    } catch (e) {
      setSaveResult({ ok: false, msg: e.message || "Update failed (run the bonus_tracker migration?)" });
    } finally {
      setTimeout(() => setSaveResult(null), 3500);
    }
  }

  // ── Per-employee email payloads ─────────────────────────────────────────────
  // Shared by Send-to-Cindy and Send-to-Crew so both emails show the same
  // breakdown. `_recordId`/`_sentToCrewAt` are UI-side bookkeeping — strip them
  // (stripPayloadMeta) before POSTing.
  function stripPayloadMeta(list) {
    return list.map(({ _recordId, _sentToCrewAt, ...e }) => e);
  }
  function buildEmployeePayloads(recs) {
    return recs.map(r => {
      const emp           = r.employee_name;
      const base          = Number(r.base_bonus) || 0;
      // LIVE auto-deductions — mirror the on-screen card, NOT the stored
      // r.auto_deductions. Excusing a miss is a metric_override that recomputes
      // the card live but does NOT re-save the record, so r.auto_deductions goes
      // stale the instant you excuse without re-saving. That silently sent
      // Tommy's June to Cindy with note misses he'd already excused (his final
      // and both emails now recompute from the same live maps the card uses).
      const liveDeds      = AUTO_TRACKED.includes(emp) ? (autoDeductionsMap[emp] || []) : [];
      const autoDeduct    = liveDeds.reduce((s, d) => s + (Number(d.total) || 0), 0);
      const customAdds    = (r.custom_items || []).filter(i => i.type === "bonus").reduce((s, i) => s + (Number(i.amount) || 0), 0);
      const customDeducts = (r.custom_items || []).filter(i => i.type === "deduction").reduce((s, i) => s + Math.abs(Number(i.amount) || 0), 0);

      // Pull review-bonus breakdown so Cindy sees which 5★ reviews drove
      // the payout (not just a lump sum). The reviewBonusesMap is computed
      // live in this component; we re-derive the per-review rows the same
      // way the card display does so the email matches what the user
      // approved on screen.
      const rb = reviewBonusesMap[r.employee_name];
      const rbTotal = Number(rb?.total) || 0;
      const reviewBonus = rb ? {
        total: rbTotal,
        totalUncapped: Number(rb.totalUncapped) || rbTotal,
        rate: rb.rate || null,
        cappedMonths: rb.cappedMonths || 0,
        // Collapse the per-row records into one entry per review so a tech
        // who earned BOTH base + mention for one review reads as a single
        // "5★ + mention" line in the email.
        items: (() => {
          const byReview = new Map();
          for (const row of (rb.rows || [])) {
            const ex = byReview.get(row.reviewId);
            if (ex) { ex.kinds.add(row.kind); ex.amount += row.amount; }
            else byReview.set(row.reviewId, {
              date: row.date, reviewer: row.reviewer, rating: row.rating,
              kinds: new Set([row.kind]), amount: row.amount,
            });
          }
          return [...byReview.values()]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map(it => ({
              date: it.date, reviewer: it.reviewer, rating: it.rating,
              kind: it.kinds.has("base") && it.kinds.has("mention") ? "both"
                  : it.kinds.has("mention") ? "mention" : "base",
              amount: it.amount,
            }));
        })(),
      } : null;

      // Referral payouts — same live map the card renders.
      const refB = referralBonusesMap[emp];
      const refTotal = Number(refB?.total) || 0;
      const referralBonus = refB ? {
        total: refTotal,
        items: (refB.rows || []).map(rr => ({
          jobNum: rr.jobNum,
          client: rr.clientName || "",
          billedMonth: rr.billedMonth,
          amount: Number(rr.amount) || 0,
          edited: !!rr.overridden,
          carried: !!rr.carried,
        })),
      } : null;

      // Recompute the final the exact way the card does (calcFinalBonus) from
      // LIVE deductions + live profit + live review + live referral, so what we
      // email always equals what's on screen — never the possibly-stale
      // r.final_bonus.
      const final = calcFinalBonus(
        { base_bonus: base, auto_deductions: liveDeds, custom_items: r.custom_items || [] },
        profitBonusesMap[emp] || [],
        rb ? { total: rbTotal } : null,
        refB ? { total: refTotal } : null,
      );

      // profitOther = "what's left after we account for everything else".
      // Review + referral are broken out separately, so subtract them here
      // so the email lines reconcile (no double counting).
      const profitOther = +(final - (base + customAdds - autoDeduct - customDeducts + rbTotal + refTotal)).toFixed(2);

      // Per-job profit rows for the tech-facing email — the same rows their
      // EmployeeCard lists. paid is ignored in the filter so re-sending an
      // already-sent month (rows now stamped paid) still lists them.
      // revenue/excess/bonusPct feed the LEAD-row detail line ("$2,767 rev ·
      // 72.4% margin · $525 × 20%") — the email builder hides job $ figures on
      // crew rows (only leads and admin see job money).
      const profitRows = (profitBonusesMap[r.employee_name] || [])
        .filter(b => isProfitBonusPayable({ ...b, paid: false }) || b.paid)
        .map(b => {
          const amount = b.manual ? (Number(b.manualAmount) || 0) : (Number(b.bonus) || 0);
          const bonusPct = Number.isFinite(Number(b.bonusPct)) ? Number(b.bonusPct) : null;
          // A corrected amount on a QUALIFYING job reads like any computed row in
          // the tech's email — no "manual adjustment" label (the correction is
          // internal). Its excess is the one the corrected amount implies, so
          // "$excess × pct%" adds up. Deductions and adjustments on
          // non-qualifying jobs keep the label.
          const asComputed = !!b.manual && !!b.qualifies && amount > 0;
          const share = b.role === "crew" ? (Number(b.weight) || 0) : 1;
          const impliedExcess = (asComputed && bonusPct > 0 && share > 0) ? amount / (bonusPct / 100) / share : null;
          return {
            jobNum: b.jobNum,
            client: b.client || "",
            role: b.role,
            closedDate: b.closedDate || null,
            revenue: Number.isFinite(Number(b.revenue)) ? Number(b.revenue) : null,
            excess: impliedExcess ?? (Number.isFinite(Number(b.excess)) ? Number(b.excess) : null),
            bonusPct,
            profitPct: Number.isFinite(Number(b.profitPct)) ? Number(b.profitPct) : null,
            manual: !!b.manual && !asComputed,
            amount,
            carriedFrom: b.carriedFrom || null,
          };
        })
        // A $0 row (e.g. a zeroed-out false profit) is never an email line —
        // if one was ever marked paid, a re-send would list it as a "$0
        // manual adjustment".
        .filter(row => Math.abs(row.amount) > 0.005);

      return {
        name:           r.employee_name,
        base,
        autoDeductions: liveDeds.filter(d => Number(d.total) > 0).map(d => ({
          label: d.label,
          total: Number(d.total) || 0,
          // Detail bits for the tech email's subline ("6 misses × $10 (capped
          // at $150) · 34 total entries") — Cindy's builder ignores them.
          misses: d.misses ?? null,
          perMiss: d.per_miss ?? null,
          cap: d.cap ?? null,
          totalEntries: d.total_entries ?? null,
        })),
        profitOther,
        profitRows,
        reviewBonus,
        referralBonus,
        customItems:    (r.custom_items || []).map(ci => ({ label: ci.label, amount: Math.abs(Number(ci.amount) || 0), type: ci.type })),
        adminNotes:     r.admin_notes || "",
        final,
        _recordId:      r.id,
        _sentToCrewAt:  r.sent_to_crew_at || null,
      };
    });
  }

  // ── Send the consolidated payroll report to Cindy ───────────────────────────
  // Only finalized bonuses for the visible month are included. No QB journal
  // entry is created (email only), so re-sends after a correction are safe.
  async function handleSendToCindy() {
    const finalizedRecs = bonusRecords.filter(r => r.finalized);
    if (finalizedRecs.length === 0) {
      setSaveResult({ ok: false, msg: "No finalized bonuses to send. Finalize at least one employee first." });
      setTimeout(() => setSaveResult(null), 3500);
      return;
    }

    const payloads  = buildEmployeePayloads(finalizedRecs); // live final + deductions (keeps _recordId)
    const employees = stripPayloadMeta(payloads);
    const grand = employees.reduce((s, e) => s + e.final, 0);

    if (!window.confirm(
      `Send a payroll bonus report to Cindy for ${employees.length} finalized employee${employees.length !== 1 ? "s" : ""} (${periodLabel})?\n\nTotal: $${grand.toLocaleString("en-CA", { maximumFractionDigits: 0 })}`
    )) return;

    setSendingReport(true);
    setSaveResult(null);
    try {
      const res = await fetch("/api/quickbooks-bonus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "emailPayrollReport", period: periodLabel, approvedBy: userEmail, employees }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Send failed");

      // Mark each included record as sent AND refresh its stored final_bonus +
      // auto_deductions to the LIVE values we just emailed — otherwise a record
      // that went stale (miss excused after its last save) keeps the old final
      // in the roll-up while the email shows the new one. Per-record because the
      // values differ. Mirrors what a manual card re-save stores.
      const sentAt = new Date().toISOString();
      for (const p of payloads) {
        await supabase
          .from("bonus_tracker")
          .update({
            sent_to_cindy_at: sentAt,
            sent_to_cindy_by: userEmail,
            final_bonus:      p.final,
            auto_deductions:  AUTO_TRACKED.includes(p.name) ? (autoDeductionsMap[p.name] || []) : [],
            updated_at:       sentAt,
          })
          .eq("id", p._recordId);
      }

      // Every payable profit-bonus row in this batch is PAID OUT now — stamp
      // paid + paid_month so it stops carrying forward and stays pinned to
      // THIS month's view. (The SNDL bug: a pay-anyway row slid into July the
      // moment June was sent, because nothing ever marked it paid.)
      const paidMonthISO = periodTab === "monthly" ? toISODate(monthStart(selectedYear, selectedMonth)) : null;
      const stampRows = [];
      for (const rec of finalizedRecs) {
        for (const b of (profitBonusesMap[rec.employee_name] || [])) {
          if (isProfitBonusPayable(b)) stampRows.push(b);
        }
      }
      if (stampRows.length) {
        const nowISO = new Date().toISOString();
        for (const b of stampRows) {
          await supabase.from("profit_bonus_overrides").upsert({
            job_num: String(b.jobNum), tech_first: String(b.techFirst), role: b.role,
            paid: true, paid_at: nowISO,
            ...(paidMonthISO ? { paid_month: paidMonthISO } : {}),
            updated_at: nowISO, updated_by: userEmail,
          }, { onConflict: "job_num,tech_first" });
        }
        setProfitBonusOverrides(prev => {
          const next = { ...prev };
          for (const b of stampRows) {
            const key = `${b.jobNum}|${b.techFirst}`;
            next[key] = {
              ...next[key],
              job_num: String(b.jobNum), tech_first: String(b.techFirst), role: b.role,
              paid: true, paid_month: paidMonthISO,
            };
          }
          return next;
        });
      }

      // Same stamping for referral payouts — a referral in this batch is paid
      // out now; without paid+paid_month it would re-carry into next month
      // forever (the exact SNDL failure mode, referral edition).
      const refStamp = [];
      for (const rec of finalizedRecs) {
        for (const rr of (referralBonusesMap[rec.employee_name]?.rows || [])) {
          if (!rr.paid) refStamp.push(rr);
        }
      }
      if (refStamp.length) {
        const nowISO = new Date().toISOString();
        for (const rr of refStamp) {
          await supabase.from("referral_payout_overrides").upsert({
            job_num: String(rr.jobNum),
            employee_name: rr.employee,
            paid: true,
            paid_amount: Number(rr.amount) || 0,
            ...(paidMonthISO ? { paid_month: paidMonthISO } : {}),
            updated_at: nowISO, updated_by: userEmail,
          }, { onConflict: "organization_id,job_num,employee_name" });
        }
        const { data: refOvFresh } = await supabase.from("referral_payout_overrides").select("*");
        setReferralOverrides(refOvFresh || []);
      }

      setSaveResult({ ok: true, msg: `Report sent to ${json.recipient || "Cindy"} — ${employees.length} bonus${employees.length !== 1 ? "es" : ""}` });
      await loadBonusRecords();
      // Same-time crew emails: pop the Send-to-Crew preview right after the
      // Cindy report goes out. Nothing sends until Dylan reviews and confirms.
      handleOpenSendToCrew(finalizedRecs);
    } catch (e) {
      setSaveResult({ ok: false, msg: e.message || "Send failed" });
    } finally {
      setSendingReport(false);
      setTimeout(() => setSaveResult(null), 4000);
    }
  }

  // ── Send each tech their own breakdown ("Send to Crew") ─────────────────────
  // Opens a preview modal first — per tech: resolved recipient address + the
  // exact line items their email will contain (admin notes are NEVER included
  // in tech emails). Send only fires from the modal's confirm button, so this
  // also works retroactively on any month (pick the month, hit Send to Crew).
  const [crewModal, setCrewModal] = useState(null);

  function handleOpenSendToCrew(recsOverride) {
    const finalizedRecs = (recsOverride || bonusRecords).filter(r => r.finalized);
    if (finalizedRecs.length === 0) {
      setSaveResult({ ok: false, msg: "No finalized bonuses for this month. Finalize at least one employee first." });
      setTimeout(() => setSaveResult(null), 3500);
      return;
    }
    const employees = buildEmployeePayloads(finalizedRecs);
    setCrewModal({ employees, recipients: null, included: new Set(employees.map(e => e.name)), sending: false, error: null });
    // Resolve recipient emails server-side (dryRun) so the preview shows the
    // real destination address for each tech before anything sends.
    fetch("/api/quickbooks-bonus", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({
        action: "emailCrewBonusBreakdowns", dryRun: true,
        period: periodLabel, employees: stripPayloadMeta(employees),
      }),
    })
      .then(r => r.json())
      .then(json => {
        if (!json.ok) throw new Error(json.error || "Could not look up crew emails");
        setCrewModal(m => m ? {
          ...m,
          recipients: json.recipients,
          // Pre-tick only techs with a resolved address.
          included: new Set((json.recipients || []).filter(r => r.email).map(r => r.name)),
        } : m);
      })
      .catch(e => setCrewModal(m => m ? { ...m, error: e.message } : m));
  }

  async function handleSendToCrew() {
    if (!crewModal || crewModal.sending) return;
    const chosen = crewModal.employees.filter(e => crewModal.included.has(e.name));
    if (chosen.length === 0) return;
    setCrewModal(m => ({ ...m, sending: true, error: null }));
    try {
      const res = await fetch("/api/quickbooks-bonus", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action: "emailCrewBonusBreakdowns",
          period: periodLabel, approvedBy: userEmail,
          employees: stripPayloadMeta(chosen),
        }),
      });
      const json = await res.json();
      const sentNames = new Set((json.sent || []).map(s => s.name));
      if (!res.ok || (!json.ok && sentNames.size === 0)) {
        throw new Error(json.error || json.failed?.[0]?.error || `Send failed (${res.status})`);
      }
      // Stamp the records that actually went out (informational — re-sends OK).
      const ids = chosen.filter(e => sentNames.has(e.name)).map(e => e._recordId).filter(Boolean);
      if (ids.length) {
        await supabase
          .from("bonus_tracker")
          .update({ sent_to_crew_at: new Date().toISOString(), sent_to_crew_by: userEmail })
          .in("id", ids);
      }
      setCrewModal(null);
      const skipBit = (json.skipped || []).length ? ` · ${json.skipped.length} skipped (no email on file)` : "";
      const failBit = (json.failed || []).length ? ` · ${json.failed.length} FAILED — ${json.failed.map(f => f.name).join(", ")}` : "";
      setSaveResult({
        ok: (json.failed || []).length === 0,
        msg: `Breakdown emailed to ${sentNames.size} tech${sentNames.size !== 1 ? "s" : ""}${skipBit}${failBit}`,
      });
      await loadBonusRecords();
      setTimeout(() => setSaveResult(null), 6000);
    } catch (e) {
      setCrewModal(m => m ? { ...m, sending: false, error: e.message || "Send failed" } : m);
    }
  }

  // ── Period label ───────────────────────────────────────────────────────────
  const periodLabel = useMemo(() => {
    if (periodTab === "monthly") return `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;
    return `Q${selectedQuarter + 1} ${selectedYear} (${MONTH_NAMES[quarterMonths(selectedYear, selectedQuarter)[0]]}–${MONTH_NAMES[quarterMonths(selectedYear, selectedQuarter)[2]]})`;
  }, [periodTab, selectedYear, selectedMonth, selectedQuarter]);

  // ── Permission guard — must have view_bonus or be admin ───────────────────
  if (!canViewBonus) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
        <div style={{ fontSize: 32, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", marginBottom: 8, color: C.accent }}>
          No Access
        </div>
        <div style={{ fontSize: 14 }}>Bonus Tracker is restricted. Ask Dylan for access.</div>
      </div>
    );
  }

  // ── Unmapped guard — non-admin with no Jobber mapping sees a helpful note ─
  if (!isAdmin && visibleEmployees.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
        <div style={{ fontSize: 32, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", marginBottom: 8, color: C.accent }}>
          Not Mapped Yet
        </div>
        <div style={{ fontSize: 14, maxWidth: 420, margin: "0 auto", lineHeight: 1.5 }}>
          Your account isn't linked to a Jobber employee yet. Ask Dylan to set your Jobber Employee in Manage Crew so your bonus shows here.
        </div>
      </div>
    );
  }

  const tabBtn = (t) => ({
    padding: "7px 18px", borderRadius: 8, border: "none", cursor: "pointer",
    fontSize: 13, fontWeight: 600,
    background: periodTab === t ? C.accent : C.surface,
    color: periodTab === t ? "#0d0f12" : C.muted,
    transition: "all 0.15s",
  });

  const viewBtn = (v) => ({
    padding: "7px 18px", borderRadius: 8, border: "none", cursor: "pointer",
    fontSize: 13, fontWeight: 600,
    background: bonusView === v ? C.accent : C.surface,
    color: bonusView === v ? "#0d0f12" : C.muted,
    transition: "all 0.15s",
  });

  return (
    <div style={{ color: C.text, fontFamily: "'IBM Plex Sans', 'Roboto', sans-serif" }}>
      {/* ── View switcher: Performance / Referrals ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        <button style={viewBtn("performance")} onClick={() => setBonusView("performance")}>Performance</button>
        <button style={viewBtn("referrals")} onClick={() => setBonusView("referrals")}>Referrals</button>
      </div>

      {/* ── Referrals view ── */}
      {bonusView === "referrals" && (
        <ReferralBonuses />
      )}

      {/* ── Performance view ── */}
      {bonusView !== "performance" ? null : <>

      {/* ── Toolbar ── */}
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.border}`,
      }}>
        {/* Monthly / Quarterly tab */}
        <div style={{ display: "flex", gap: 4 }}>
          <button style={tabBtn("monthly")} onClick={() => setPeriodTab("monthly")}>Monthly</button>
          <button style={tabBtn("quarterly")} onClick={() => setPeriodTab("quarterly")}>Quarterly</button>
        </div>

        {/* Year */}
        <select
          value={selectedYear}
          onChange={e => setSelectedYear(Number(e.target.value))}
          style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, cursor: "pointer" }}
        >
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {/* Month picker */}
        {periodTab === "monthly" && (
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}
            style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, cursor: "pointer" }}
          >
            {MONTH_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}
          </select>
        )}

        {/* Quarter picker */}
        {periodTab === "quarterly" && (
          <select
            value={selectedQuarter}
            onChange={e => setSelectedQuarter(Number(e.target.value))}
            style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, cursor: "pointer" }}
          >
            <option value={0}>Q1 (Jan–Mar)</option>
            <option value={1}>Q2 (Apr–Jun)</option>
            <option value={2}>Q3 (Jul–Sep)</option>
            <option value={3}>Q4 (Oct–Dec)</option>
          </select>
        )}

        {/* Show archived employees toggle (admins only) */}
        {isAdmin && (
          <label style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 12, color: C.muted, cursor: "pointer",
            userSelect: "none",
          }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            Show archived
          </label>
        )}

        <div style={{ marginLeft: "auto", fontSize: 12, color: C.muted }}>
          {periodLabel}
          {(dataLoading || recordsLoading) && <span style={{ marginLeft: 8, color: C.accent }}>Loading…</span>}
        </div>
      </div>

      {/* ── Data-load failure banner ─────────────────────────────────────── */}
      {/* This page is where the month's bonuses get paid from. A failed load  */}
      {/* used to be swallowed, leaving empty auto-deductions and defaulted    */}
      {/* config that LOOK like real zeroes. Say it plainly instead.           */}
      {dataError && (
        <div style={{
          padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: C.redDim, border: `1px solid ${C.red}`, color: C.red,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontWeight: 700 }}>⚠ Bonus data failed to load — the numbers below are INCOMPLETE.</span>
          <span style={{ color: C.muted }}>Do not pay from this page until it loads cleanly.</span>
          <code style={{ fontSize: 11, opacity: 0.8, wordBreak: "break-all" }}>{dataError}</code>
          <button
            onClick={() => { setDataError(null); loadJobberData(); }}
            style={{
              marginLeft: "auto", padding: "6px 14px", borderRadius: 6, fontSize: 12,
              fontWeight: 600, cursor: "pointer", background: C.red, color: "#fff",
              border: "none",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Save result toast ── */}
      {saveResult && (
        <div style={{
          padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: saveResult.ok ? C.greenDim : C.redDim,
          border: `1px solid ${saveResult.ok ? C.green : C.red}30`,
          color: saveResult.ok ? C.green : C.red,
        }}>
          {saveResult.ok ? "✓ " : "✗ "}{saveResult.msg}
        </div>
      )}

      {/* ── Quarterly summary rollup ── */}
      {/* Cross-employee leaderboard — only visible to admins/owners or       */}
      {/* crew explicitly granted "view_all_bonus_amounts" in Manage Crew.    */}
      {periodTab === "quarterly" && (isAdmin || canSeeAllBonusAmounts) && (
        <QuarterlySummary
          year={selectedYear}
          quarter={selectedQuarter}
          monthlyRecords={bonusRecords}
          employeeList={activeEmployees}
        />
      )}

      {/* Non-admin / no cross-employee access: personal quarterly roll-up only. */}
      {periodTab === "quarterly" && !(isAdmin || canSeeAllBonusAmounts) && visibleEmployees.length > 0 && (
        <PersonalQuarterlySummary
          year={selectedYear}
          quarter={selectedQuarter}
          monthlyRecords={bonusRecords}
          employeeName={visibleEmployees[0]}
        />
      )}

      {/* ── Monthly: employee cards ── */}
      {periodTab === "monthly" && (
        <>
          {/* Period summary strip — totals across whoever's currently visible.
              Pre-finalized = live calculation of base+profit+review-deductions
              for every visible card. Finalized = sum of saved final_bonus on
              records that have been finalized. They converge as cards get
              finalized; when every visible employee is finalized, the two
              tiles should match. Recomputes whenever the period filter,
              employee filter, or any underlying bonus number changes. */}
          {(() => {
            let baseTotal = 0;
            let profitTotal = 0;
            let reviewTotal = 0;
            let customAddsTotal = 0;
            let customDeductsTotal = 0;
            let autoDeductTotal = 0;
            let preFinalTotal = 0;
            let finalizedTotal = 0;
            let finalizedCount = 0;
            for (const emp of visibleEmployees) {
              const rec = getRecord(emp, bonusRecords);
              const defBase = BASE_500_EMPLOYEES.has(emp) ? 500 : 0;
              const base = Number(rec?.base_bonus ?? defBase) || 0;
              const customAdds = (rec?.custom_items || []).filter(i => i.type === "bonus").reduce((s, i) => s + (Number(i.amount) || 0), 0);
              const customDeducts = (rec?.custom_items || []).filter(i => i.type === "deduction").reduce((s, i) => s + Math.abs(Number(i.amount) || 0), 0);
              const autoDeduct = (AUTO_TRACKED.includes(emp) ? (autoDeductionsMap[emp] || []) : [])
                .reduce((s, d) => s + (Number(d.total) || 0), 0);
              // Same payable-or-paid rule as calcFinalBonus — otherwise a sent month
              // (rows stamped paid) reads Profit $0 while its cards still show them.
              const profit = (profitBonusesMap[emp] || []).filter(b => isProfitBonusPayable(b) || b.paid).reduce((s, b) => s + (Number(b.bonus) || 0), 0);
              const review = Number(reviewBonusesMap[emp]?.total) || 0;
              // Referral term — cards include it via calcFinalBonus, so the
              // pre-final estimate must too or the drift check cries wolf
              // every referral month.
              const referral = Number(referralBonusesMap[emp]?.total) || 0;
              baseTotal += base;
              profitTotal += profit;
              reviewTotal += review;
              customAddsTotal += customAdds;
              customDeductsTotal += customDeducts;
              autoDeductTotal += autoDeduct;
              preFinalTotal += base + customAdds + profit + review + referral - autoDeduct - customDeducts;
              if (rec?.finalized) {
                finalizedTotal += Number(rec.final_bonus) || 0;
                finalizedCount++;
              }
            }
            const empCount = visibleEmployees.length;
            const allFinalized = empCount > 0 && finalizedCount === empCount;
            const matchedColor = allFinalized && Math.abs(preFinalTotal - finalizedTotal) < 0.5 ? C.green : C.muted;
            const Tile = ({ label, value, sub, color, accent }) => (
              <div style={{
                flex: "1 1 0", minWidth: 140,
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
                padding: "12px 14px",
                ...(accent ? { borderLeft: `4px solid ${accent}` } : {}),
              }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color || C.text, marginTop: 2 }}>
                  ${Math.round(value).toLocaleString("en-CA", { maximumFractionDigits: 0 })}
                </div>
                {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
              </div>
            );
            return (
              <div style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Tile label="Base" value={baseTotal} accent={C.muted}
                      sub={`${empCount} employee${empCount !== 1 ? "s" : ""}`} />
                <Tile label="Profit" value={profitTotal} color={profitTotal > 0 ? C.green : C.muted} accent={C.green} />
                <Tile label="Review" value={reviewTotal} color={reviewTotal > 0 ? C.green : C.muted} accent={C.green} />
                <Tile label="Pre-finalized" value={preFinalTotal} color={C.accent} accent={C.accent}
                      sub={(autoDeductTotal + customDeductsTotal > 0)
                        ? `after −$${Math.round(autoDeductTotal + customDeductsTotal).toLocaleString("en-CA", { maximumFractionDigits: 0 })} deductions`
                        : (customAddsTotal > 0 ? `+ $${Math.round(customAddsTotal).toLocaleString("en-CA", { maximumFractionDigits: 0 })} custom` : "before finalize")} />
                <Tile label="Finalized" value={finalizedTotal} color={matchedColor} accent={matchedColor}
                      sub={empCount > 0
                        ? (allFinalized
                            ? (Math.abs(preFinalTotal - finalizedTotal) < 0.5 ? "✓ matches pre-final" : `${Math.round(preFinalTotal - finalizedTotal)} drift — recheck cards`)
                            : `${finalizedCount} of ${empCount} cards`)
                        : "no employees"} />
              </div>
            );
          })()}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {periodLabel}{isAdmin ? " — Employee Bonuses" : ""}
            </div>
            {isAdmin && hasPermission("edit_bonus") && (() => {
              const finalizedCount = bonusRecords.filter(r => r.finalized).length;
              const unsentCount    = bonusRecords.filter(r => r.finalized && !r.sent_to_cindy_at).length;
              const crewUnsent     = bonusRecords.filter(r => r.finalized && !r.sent_to_crew_at).length;
              return (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={handleSendToCindy}
                    disabled={sendingReport || finalizedCount === 0}
                    title={finalizedCount === 0 ? "Finalize at least one employee first" : `Email Cindy the ${finalizedCount} finalized bonus${finalizedCount !== 1 ? "es" : ""} for ${periodLabel}`}
                    style={{
                      padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.accent}`,
                      cursor: (sendingReport || finalizedCount === 0) ? "not-allowed" : "pointer",
                      fontSize: 12, fontWeight: 700,
                      background: finalizedCount === 0 ? C.surface : C.accent,
                      color: finalizedCount === 0 ? C.muted : "#0d0f12",
                      opacity: sendingReport ? 0.6 : 1,
                    }}
                  >
                    {sendingReport
                      ? "Sending…"
                      : `Send Finalized to Cindy (${finalizedCount})${unsentCount > 0 && unsentCount !== finalizedCount ? ` · ${unsentCount} new` : ""}`}
                  </button>
                  <button
                    onClick={() => handleOpenSendToCrew()}
                    disabled={finalizedCount === 0 || !!crewModal}
                    title={finalizedCount === 0
                      ? "Finalize at least one employee first"
                      : `Preview + email each tech their OWN ${periodLabel} breakdown (opens a review screen first)`}
                    style={{
                      padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.blue}`,
                      cursor: (finalizedCount === 0 || crewModal) ? "not-allowed" : "pointer",
                      fontSize: 12, fontWeight: 700,
                      background: "transparent",
                      color: finalizedCount === 0 ? C.muted : C.blue,
                    }}
                  >
                    {`Send to Crew (${finalizedCount})${crewUnsent > 0 && crewUnsent !== finalizedCount ? ` · ${crewUnsent} new` : ""}`}
                  </button>
                </div>
              );
            })()}
          </div>
          {/* ── Send-to-Crew preview modal — review every email before it goes ── */}
          {crewModal && createPortal(
            <div
              onClick={() => !crewModal.sending && setCrewModal(null)}
              style={{
                position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.72)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
                  padding: "22px 24px", maxWidth: 680, width: "100%", maxHeight: "85vh",
                  overflowY: "auto", color: C.text, fontFamily: "'Roboto', sans-serif",
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, marginBottom: 4 }}>
                  Send Bonus Breakdowns to Crew — {periodLabel}
                </div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
                  Each tech gets an email with <b>only their own</b> breakdown — exactly the lines shown below,
                  sent to the address shown. Your admin notes are <b>not</b> included. Untick anyone you don't
                  want emailed, then hit Send.
                </div>

                {crewModal.error && (
                  <div style={{
                    padding: "10px 14px", borderRadius: 8, marginBottom: 12,
                    background: `${C.red}15`, border: `1px solid ${C.red}40`,
                    fontSize: 13, color: C.red,
                  }}>
                    {crewModal.error}
                  </div>
                )}
                {!crewModal.recipients && !crewModal.error && (
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Looking up crew email addresses…</div>
                )}

                {crewModal.employees.map(e => {
                  const rcpt = crewModal.recipients?.find(r => r.name === e.name);
                  const noEmail = !!crewModal.recipients && !rcpt?.email;
                  const included = crewModal.included.has(e.name) && !noEmail;
                  const rb = e.reviewBonus;
                  return (
                    <div key={e.name} style={{
                      border: `1px solid ${included ? C.accent + "40" : C.border}`,
                      borderRadius: 10, padding: "12px 14px", marginBottom: 10,
                      background: included ? `${C.accent}08` : C.card,
                      opacity: noEmail ? 0.6 : 1,
                    }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: noEmail ? "default" : "pointer" }}>
                        <input
                          type="checkbox"
                          checked={included}
                          disabled={noEmail || crewModal.sending}
                          onChange={ev => {
                            const on = ev.target.checked;
                            setCrewModal(m => {
                              const next = new Set(m.included);
                              if (on) next.add(e.name); else next.delete(e.name);
                              return { ...m, included: next };
                            });
                          }}
                          style={{ accentColor: C.accent, width: 15, height: 15 }}
                        />
                        <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
                          {e.name}
                          <span style={{ fontWeight: 400, fontSize: 11, color: noEmail ? C.red : C.muted, marginLeft: 8 }}>
                            {crewModal.recipients
                              ? (rcpt?.email ? `→ ${rcpt.email}` : "no email on file — will be skipped")
                              : "…"}
                          </span>
                        </span>
                        <span style={{ fontWeight: 800, fontSize: 15, color: C.green }}>
                          ${e.final.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                        </span>
                      </label>
                      {/* Mirrors the EMAIL content exactly — two-line rows like the
                          EmployeeCard. Job $ figures only on rows they LED. */}
                      <div style={{ marginLeft: 25, marginTop: 8, fontSize: 12, lineHeight: 1.5 }}>
                        {(() => {
                          const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                          const shortDate = (v) => {
                            const d = v ? new Date(v) : null;
                            return d && !isNaN(d.getTime()) ? d.toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "";
                          };
                          const jobDetail = (pr) => {
                            const bits = [shortDate(pr.closedDate)];
                            if (pr.manual) bits.push("manual adjustment");
                            else if (pr.role === "lead") {
                              if (pr.revenue != null) bits.push(`$${Math.round(pr.revenue).toLocaleString("en-CA")} rev`);
                              if (pr.profitPct != null) bits.push(`${Number(pr.profitPct).toFixed(1)}% margin`);
                              if (pr.excess != null && pr.bonusPct != null) bits.push(`$${Math.round(pr.excess).toLocaleString("en-CA")} × ${pr.bonusPct}%`);
                            } else {
                              if (pr.profitPct != null) bits.push(`${Number(pr.profitPct).toFixed(1)}% margin`);
                              bits.push("crew share");
                            }
                            if (pr.carriedFrom) bits.push(`carried from ${pr.carriedFrom}`);
                            return bits.filter(Boolean).join(" · ");
                          };
                          const dedDetail = (d) => {
                            const bits = [];
                            if (d.misses != null && d.perMiss != null) bits.push(`${d.misses} miss${d.misses !== 1 ? "es" : ""} × $${d.perMiss}${d.cap != null && Number(d.total) >= Number(d.cap) ? ` (capped at $${d.cap})` : ""}`);
                            if (d.totalEntries) bits.push(`${d.totalEntries} total entries`);
                            return bits.join(" · ");
                          };
                          const line = (title, detail, right, color, key) => (
                            <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0" }}>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ color: C.text, fontWeight: 600 }}>{title}</span>
                                {detail && <span style={{ display: "block", fontSize: 11, color: C.muted }}>{detail}</span>}
                              </span>
                              <span style={{ color, fontWeight: 700, whiteSpace: "nowrap" }}>{right}</span>
                            </div>
                          );
                          const rbItems = (rb && Number(rb.total) > 0.5) ? (rb.items || []) : [];
                          const mentions = rbItems.filter(it => it.kind === "mention" || it.kind === "both").length;
                          const profitTotal = (e.profitRows || []).length
                            ? e.profitRows.reduce((s, pr) => s + (Number(pr.amount) || 0), 0)
                            : (Number(e.profitOther) || 0);
                          const dedTotal = (e.autoDeductions || []).reduce((s, d) => s + (Number(d.total) || 0), 0);
                          return (
                            <>
                              {/* Crew get no base — hide the $0 line entirely (Dylan 07-23) */}
                              {Number(e.base) > 0 && line("Base bonus", null, money(e.base), C.text, "base")}
                              {(e.autoDeductions || []).map((d, i) => line(d.label, dedDetail(d), `-${money(d.total)}`, C.red, `d${i}`))}
                              {(e.profitRows || []).length > 0
                                ? e.profitRows.map((pr, i) => line(
                                    <>#{pr.jobNum} · {pr.client}{pr.role === "lead" ? <span style={{ color: C.accent, fontSize: 10, fontWeight: 700 }}> LEAD</span> : null}</>,
                                    jobDetail(pr), `+${money(pr.amount)}`, C.green, `p${i}`))
                                : Number(e.profitOther) > 0.5
                                  ? line("Profit bonuses", null, `+${money(e.profitOther)}`, C.green, "po")
                                  : null}
                              {rbItems.map((it, i) => line(
                                <><span style={{ color: C.yellow, letterSpacing: 1 }}>{"★".repeat(Math.max(1, Math.min(5, Number(it.rating) || 5)))}</span> {it.reviewer || "(anon)"}<span style={{ color: C.muted, fontWeight: 400 }}> · {shortDate(it.date)}</span></>,
                                it.kind === "both" ? "5★ + mention" : it.kind === "mention" ? "mention" : "5★",
                                `+${money(it.amount)}`, C.green, `r${i}`))}
                              {(e.referralBonus?.items || []).map((it, i) => line(
                                <>Referral: {it.client || "client"} <span style={{ color: C.muted, fontWeight: 400 }}>· #{it.jobNum}</span></>,
                                `billed ${it.billedMonth}${it.carried ? " · carried forward" : ""}${it.edited ? " · edited" : ""}`,
                                `+${money(it.amount)}`, C.green, `rf${i}`))}
                              {(e.customItems || []).map((ci, i) => line(ci.label, null, `${ci.type === "deduction" ? "-" : "+"}${money(ci.amount)}`, ci.type === "deduction" ? C.red : C.green, `c${i}`))}
                              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 6, paddingTop: 6, fontSize: 11, color: C.muted }}>
                                Base {money(e.base)}
                                {dedTotal > 0 && <> · Deductions -{money(dedTotal)}</>}
                                {profitTotal > 0.005 && <> · Profit +{money(profitTotal)}</>}
                                {rbItems.length > 0 && <> · Reviews ({rbItems.length}× 5★{mentions ? ` · ${mentions}× mention` : ""}) +{money(rb.total)}</>}
                                {Number(e.referralBonus?.total) > 0.005 && <> · Referrals +{money(e.referralBonus.total)}</>}
                                <span style={{ color: C.green, fontWeight: 700 }}> · Final {money(e.final)}</span>
                              </div>
                              {e._sentToCrewAt && (
                                <div style={{ color: C.purple, fontSize: 11, marginTop: 4 }}>
                                  Already emailed {new Date(e._sentToCrewAt).toLocaleDateString("en-CA")} — sending again is fine (re-send).
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
                  <button
                    onClick={() => setCrewModal(null)}
                    disabled={crewModal.sending}
                    style={{
                      padding: "9px 18px", borderRadius: 8, border: `1px solid ${C.border}`,
                      background: "transparent", color: C.muted, fontSize: 13, fontWeight: 600,
                      cursor: crewModal.sending ? "not-allowed" : "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  {(() => {
                    const sendCount = crewModal.employees.filter(e =>
                      crewModal.included.has(e.name) &&
                      crewModal.recipients?.find(r => r.name === e.name)?.email
                    ).length;
                    const disabled = crewModal.sending || !crewModal.recipients || sendCount === 0;
                    return (
                      <button
                        onClick={handleSendToCrew}
                        disabled={disabled}
                        style={{
                          padding: "9px 22px", borderRadius: 8, border: "none",
                          background: disabled ? `${C.accent}50` : C.accent,
                          color: "#0d0f12", fontSize: 13, fontWeight: 700,
                          cursor: disabled ? "not-allowed" : "pointer",
                        }}
                      >
                        {crewModal.sending
                          ? "Sending…"
                          : `Send ${sendCount} Email${sendCount !== 1 ? "s" : ""}`}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>,
            document.body
          )}

          {isAdmin && (
            <ExcludedJobsPanel
              excludedJobs={excludedJobs}
              perEmployeeExclusions={perEmployeeExclusions}
              onExclude={jn => toggleJobExcluded(jn, true)}
              onRestore={jn => toggleJobExcluded(jn, false)}
              onRestoreEmployee={(jobNum, techFirst, role) => handleUpdateBonusOverride(jobNum, techFirst, role, { excluded: false })}
              busy={excludingJob}
            />
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
            {visibleEmployees.map(emp => (
              <EmployeeCard
                key={emp}
                employeeName={emp}
                record={getRecord(emp, bonusRecords)}
                autoDeductions={autoDeductionsMap[emp] || []}
                profitBonuses={profitBonusesMap[emp] || []}
                reviewBonus={reviewBonusesMap[emp] || null}
                referralBonus={referralBonusesMap[emp] || null}
                onEditReferralAmount={isAdmin ? handleEditReferralAmount : undefined}
                onUpdateBonusOverride={handleUpdateBonusOverride}
                onExcludeJob={isAdmin ? (jobNum => toggleJobExcluded(jobNum, true)) : undefined}
                onExcludeEmployeeJob={isAdmin ? ((jobNum, techFirst, role) => handleUpdateBonusOverride(jobNum, techFirst, role, { excluded: true })) : undefined}
                isAutoTracked={AUTO_TRACKED.includes(emp)}
                periodLabel={periodLabel}
                isQuarterly={false}
                onSave={handleSave}
                saving={saving}
                readOnly={!hasPermission("edit_bonus")}
                isAdmin={isAdmin}
                onRefresh={refreshAll}
                onSetFinalized={handleSetFinalized}
              />
            ))}
          </div>
        </>
      )}

      </>}
    </div>
  );
}
